import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Forward,
  Inbox,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  Reply,
  ReplyAll,
  Star,
  Trash2,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MailAttachmentPreview } from "@/components/MailAttachmentPreview";
import { MailBodyHtml } from "@/components/MailBodyHtml";
import { MailInviteRsvpCard } from "@/components/MailInviteRsvpCard";
import { coerceAttachmentContentId } from "@/mail/inlineImages";
import { isCalendarAttachment } from "@/mail/ics";
import { MailComposePanel, type SavedDraftInfo, type SentMailInfo, type ScheduledSendInfo } from "@/components/MailComposePanel";
import { MailListRow } from "@/components/MailListRow";
import { MailReaderErrorBoundary } from "@/components/MailReaderErrorBoundary";
import { ModuleDrawerAdd } from "@/components/ModuleDrawerAdd";
import { canNativePreviewFile, isHubIosShell, isHubNativeShell } from "@/lib/hubShell";
import { matchesHubSearch, useDebouncedValue, useHubSearchQuery } from "@/lib/hubSearch";
import { formatListWhen } from "@/lib/listWhen";
import {
  ApiError,
  api,
  apiBase,
  getToken,
  isAuthApiError,
  type MailAccount,
  type MailAttachment,
  type MailListResponse,
  type MailMessage,
  type MailThread,
} from "@/lib/api";
import {
  buildAttachmentCacheKey,
  readCachedAttachmentObjectUrl,
  writeCachedAttachment,
} from "@/mail/attachmentCache";
import {
  absolutizeAttachmentHref,
  attachmentOpenUrl,
  canPreviewAttachment,
  downloadAttachmentHref,
  isDirectlyOpenableHref,
  openWithAttachmentHref,
  isImageAttachment,
  isPdfAttachment,
  pickOpenableAttachmentHref,
} from "@/mail/attachmentOpen";
import {
  applyServerAccountLabels,
  CONNECTION_DISPLAY_NAMES_EVENT,
  resolveAccountLabel,
} from "@/mail/connectionLabels";
import {
  fetchAccountFolders,
  isAccountFolderSyncEnabled,
  MAIL_SYNC_FOLDERS_EVENT,
  normalizeMailFolderId,
  readFolderSyncMap,
  STANDARD_MAIL_FOLDERS,
  type MailProviderFolder,
} from "@/mail/accountFolders";
import {
  buildComposeDraft,
  draftFromExistingMessage,
  emptyComposeDraft,
  isDraftMessage,
  type ComposeDraft,
  type ComposeMode,
} from "@/mail/composeDraft";
import { hydrateDraftAttachments } from "@/mail/hydrateDraftAttachments";
import {
  displayMailFrom,
  formatMailAddressListFull,
  formatMailAddressListShort,
  formatMailContact,
  formatMailContactFull,
  formatMailContactShort,
  formatMailFromFull,
  mailContactEmail,
  mailContactEmails,
} from "@/mail/formatContact";
import { escapeAndLinkify, escapeHtml, looksLikeHtml, plainToHtml, stripHtml } from "@/mail/htmlBody";
import {
  coerceMailAddressLists,
  mailDetailBccDisplay,
  mailDetailCcDisplay,
  messageBccLineShort,
  messageCcLineShort,
  messageRecipientLineFull,
  pickAddressField,
} from "@/mail/messageReaderFields";
import {
  filterThreadsForAccountView,
  mergeListMeta,
  mergeNewestWithCachedOlder,
  readCachedMailAccounts,
  readCachedMailDetail,
  readCachedMailList,
  readMailBootstrap,
  readMailNav,
  requestPersistentStorage,
  writeCachedMailAccounts,
  writeCachedMailDetail,
  writeCachedMailList,
  writeMailNav,
} from "@/mail/offlineCache";
import {
  isMailThreadFullyExpanded,
  isMailThreadHydrated,
  markThreadDetailExpanded,
  messageHasRealBody,
  shouldCacheMailDetail,
} from "@/mail/threadHydrate";
import {
  inboxChainGap,
  listChainCount,
  messageIsSent,
  messagesForMailFolder,
  threadAccountEmail,
} from "@/mail/threadView";
import { syncMailWindow } from "@/mail/windowSync";
import { GMAIL_LIST_RATE_LIMIT_BANNER } from "@/mail/mailListPage";
import {
  buildMailSearchParams,
  findThreadForMailUrlId,
  mailSearchParamsEqual,
  parseMailSearchParams,
  stubThreadFromMailUrl,
  threadApiId,
  threadRowKey,
} from "@/mail/mailUrl";
import { PaneResizer } from "@/components/PaneResizer";
import {
  MAIL_CLOCK_FORMAT_EVENT,
  MAIL_CONVERSATIONS_EVENT,
  MAIL_SWIPE_EVENT,
  MAIL_THREAD_ORDER_EVENT,
  MAIL_VIEW_EVENT,
  defaultCollapsedBodies,
  mergeCollapsedBodies,
  orderMailThreadMessages,
  readMailClockFormat,
  readMailConversations,
  readMailSwipeLeft,
  readMailSwipeRight,
  readMailThreadOrder,
  readMailView,
  writeMailConversations,
  type MailClockFormatId,
  type MailSwipeActionId,
  type MailThreadOrderId,
  type MailViewId,
} from "@/mail/prefs";

const PHONE_MQ = "(max-width: 980px)";
const SWIPE_COMMIT_RATIO = 0.28;
const SWIPE_COMMIT_MIN_PX = 72;
const SWIPE_ANIM_MS = 180;
const PREFETCH_CONCURRENCY = 3;
/** First paint: ask API for a bounded page (Lightsail paginates Gmail toward this). */
const MAIL_LIST_LIMIT = 50;
const MAIL_LIST_TIMEOUT_MS = 20_000;
const MAIL_NEW_MAIL_POLL_MS = 30_000;
const MAIL_ACCOUNTS_TIMEOUT_MS = 10_000;

/** Default Hub folders when provider sync is off for an account. */
const MAIL_FOLDERS = STANDARD_MAIL_FOLDERS;

type MailFolderId = string;

function foldersForAccountNav(
  accountId: string,
  providerFolders: Record<string, MailProviderFolder[]>,
): MailProviderFolder[] {
  if (accountId === "all" || !isAccountFolderSyncEnabled(accountId)) {
    return MAIL_FOLDERS;
  }
  const synced = providerFolders[accountId];
  if (!synced?.length) return MAIL_FOLDERS;
  return mergeStandardMailFolders(synced);
}

const STANDARD_FOLDER_IDS = new Set(STANDARD_MAIL_FOLDERS.map((folder) => folder.id));

/** Provider sync can return a short list; always keep WorkSuite system folders in nav. */
function mergeStandardMailFolders(synced: MailProviderFolder[]): MailProviderFolder[] {
  const byId = new Map<string, MailProviderFolder>();
  for (const folder of STANDARD_MAIL_FOLDERS) byId.set(folder.id, folder);
  for (const folder of synced) {
    if (!folder.id) continue;
    if (STANDARD_FOLDER_IDS.has(folder.id)) {
      const base = byId.get(folder.id);
      byId.set(folder.id, {
        ...base,
        ...folder,
        label: folder.label || base?.label || folder.id,
      });
    } else {
      byId.set(folder.id, folder);
    }
  }
  const ordered = STANDARD_MAIL_FOLDERS.map((folder) => byId.get(folder.id)).filter(
    (folder): folder is MailProviderFolder => Boolean(folder),
  );
  const custom = synced.filter((folder) => folder.id && !STANDARD_FOLDER_IDS.has(folder.id));
  return [...ordered, ...custom];
}

/** Sort fields — one row each; direction toggles via ↑/↓ (not duplicate Newest/Oldest rows). */
const SORT_FIELDS = [
  { id: "date", label: "Date" },
  { id: "from", label: "From" },
  { id: "subject", label: "Subject" },
] as const;

/**
 * Direction convention (shown on trigger + active row):
 * - ↓ desc = Date newest-first; From/Subject Z→A
 * - ↑ asc  = Date oldest-first; From/Subject A→Z
 * Default: Date ↓ (newest on top).
 */
type SortFieldId = (typeof SORT_FIELDS)[number]["id"];
type SortDirection = "desc" | "asc";

const FILTER_OPTIONS = [
  { id: "all", label: "All Messages" },
  { id: "unread", label: "Unread" },
  { id: "starred", label: "Starred" },
  { id: "attachment", label: "Has Attachment" },
] as const;

type FilterId = (typeof FILTER_OPTIONS)[number]["id"];

function sortDirGlyph(dir: SortDirection): string {
  return dir === "desc" ? "↓" : "↑";
}

function sortDirAria(field: SortFieldId, dir: SortDirection): string {
  if (field === "date") {
    return dir === "desc" ? "newest first" : "oldest first";
  }
  return dir === "desc" ? "Z to A" : "A to Z";
}

const NAV_COLLAPSE_KEY = "atw-hub-mail-nav-collapsed";

function displayFrom(
  item: {
    fromName?: unknown;
    fromEmail?: unknown;
    from?: unknown;
    participants?: unknown;
    messages?: MailMessage[];
  },
  accountFallback?: string,
) {
  return displayMailFrom(item, accountFallback);
}

function safeText(value: unknown): string {
  if (typeof value === "string") return value;
  if (value == null) return "";
  return formatMailContact(value);
}

/** Pull bodyHtml / bodyText from common Lightsail + provider field names. */
function extractMessageBodies(message: MailMessage): { bodyHtml?: string; bodyText?: string } {
  const raw = message as MailMessage & {
    html?: unknown;
    text?: unknown;
    body?: unknown;
    content?: unknown;
    htmlBody?: unknown;
    html_body?: unknown;
    textBody?: unknown;
    plaintext_body?: unknown;
    plaintextBody?: unknown;
    plainText?: unknown;
  };
  const candidates = [
    raw.bodyHtml,
    raw.htmlBody,
    raw.html_body,
    raw.html,
    raw.body,
    raw.content,
  ];
  let bodyHtml: string | undefined;
  for (const value of candidates) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    if (
      value === raw.bodyHtml ||
      value === raw.htmlBody ||
      value === raw.html_body ||
      value === raw.html ||
      looksLikeHtml(trimmed)
    ) {
      bodyHtml = value;
      break;
    }
  }
  // Prefer real body fields — never treat Gmail's whitespace-collapsed snippet as bodyText
  // (snippet turns "Heyy\n\nOn … wrote:" into one mashed line in the reader).
  const textCandidates = [
    raw.bodyText,
    raw.textBody,
    raw.plaintext_body,
    raw.plaintextBody,
    raw.plainText,
    raw.text,
    raw.body,
    raw.content,
  ];
  let bodyText: string | undefined;
  for (const value of textCandidates) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    if (bodyHtml && value === bodyHtml) continue;
    if (
      looksLikeHtml(trimmed) &&
      value !== raw.bodyText &&
      value !== raw.textBody &&
      value !== raw.plaintext_body &&
      value !== raw.plaintextBody &&
      value !== raw.plainText &&
      value !== raw.text
    ) {
      continue;
    }
    bodyText = value;
    break;
  }
  if (!bodyText && typeof raw.bodyText === "string") bodyText = raw.bodyText;
  if (!bodyText) bodyText = safeText(raw.bodyText) || undefined;
  return { bodyHtml, bodyText };
}

/** Normalize API address objects so reader/list never render bare `{name,email}`. */
function coerceMailMessage(message: MailMessage, fallbackId: string): MailMessage {
  const fromFull = formatMailFromFull(message);
  const fromName =
    formatMailContact(message.fromName) ||
    formatMailContactShort(message.from) ||
    undefined;
  const fromEmail =
    mailContactEmail(message.fromEmail) ||
    mailContactEmail(message.from) ||
    mailContactEmail(message.fromName) ||
    mailContactEmails(message.from)[0] ||
    undefined;
  const from = fromFull || formatMailContact(message.from) || undefined;
  const raw = message as MailMessage & Record<string, unknown>;
  const toRaw = pickAddressField(raw, "to");
  const addressLists = coerceMailAddressLists(message);
  const toEmails = message.toEmails?.length ? message.toEmails : mailContactEmails(toRaw);
  const toList = Array.isArray(toRaw)
    ? toRaw.map(formatMailContactFull).filter(Boolean)
    : formatMailAddressListFull(toRaw);
  const { bodyHtml, bodyText } = extractMessageBodies(message);
  const date =
    coerceDateValue(message.date) ||
    coerceDateValue(raw.at) ||
    coerceDateValue(raw.receivedAt) ||
    coerceDateValue(raw.internalDate) ||
    coerceDateValue(raw.sentAt) ||
    coerceDateValue(raw.createdAt) ||
    message.date;
  const attachments = Array.isArray(message.attachments)
    ? message.attachments.map((file) => {
        const contentId = coerceAttachmentContentId(
          file as MailAttachment & Record<string, unknown>,
        );
        return contentId && contentId !== file.contentId ? { ...file, contentId } : file;
      })
    : message.attachments;
  return {
    ...message,
    id: String(message.id ?? fallbackId),
    subject: safeText(message.subject) || undefined,
    snippet: safeText(message.snippet) || undefined,
    fromName,
    fromEmail,
    from,
    to: toList || undefined,
    cc: addressLists.cc,
    bcc: addressLists.bcc,
    toEmails: toEmails.length ? toEmails : undefined,
    ccEmails: addressLists.ccEmails,
    bccEmails: addressLists.bccEmails,
    date,
    bodyText,
    bodyHtml,
    attachments,
  };
}

/** Coerce ISO / epoch-ms / epoch-sec / Date into a parseable date string. */
function coerceDateValue(value: unknown): string | undefined {
  if (value == null || value === "") return undefined;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (/^\d+$/.test(trimmed)) return coerceDateValue(Number(trimmed));
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? undefined : trimmed;
  }
  return undefined;
}

function threadDateValue(thread: MailThread, messages: MailMessage[]): string | undefined {
  const raw = thread as MailThread & Record<string, unknown>;
  const latest = pickNewestMessage(messages);
  return (
    coerceDateValue(thread.date) ||
    coerceDateValue(raw.at) ||
    coerceDateValue(raw.receivedAt) ||
    coerceDateValue(raw.internalDate) ||
    coerceDateValue(raw.lastMessageAt) ||
    coerceDateValue(raw.lastMessageDate) ||
    coerceDateValue(raw.updatedAt) ||
    coerceDateValue(raw.sentAt) ||
    (latest ? messageDateValue(latest, thread) : undefined)
  );
}

function pickNewestMessage(messages: MailMessage[]): MailMessage | undefined {
  if (!messages.length) return undefined;
  return messages.reduce<MailMessage | null>((best, message) => {
    if (!best) return message;
    const bestAt = messageDateValue(best) ? Date.parse(messageDateValue(best)!) : 0;
    const at = messageDateValue(message) ? Date.parse(messageDateValue(message)!) : 0;
    return at >= bestAt ? message : best;
  }, null) || undefined;
}

function threadSnippetValue(thread: MailThread, messages: MailMessage[]): string | undefined {
  const raw = thread as MailThread & Record<string, unknown>;
  const latest = pickNewestMessage(messages);
  return (
    safeText(thread.snippet) ||
    safeText(raw.preview) ||
    safeText(raw.snippetText) ||
    safeText(raw.excerpt) ||
    safeText(latest?.snippet) ||
    safeText(latest?.bodyText) ||
    undefined
  );
}

function threadMessageCountValue(thread: MailThread, messages: MailMessage[]): number {
  const raw = thread as MailThread & Record<string, unknown>;
  const candidates = [
    thread.messageCount,
    raw.messagesCount,
    raw.message_count,
    raw.threadCount,
    raw.count,
  ];
  for (const value of candidates) {
    if (typeof value === "number" && Number.isFinite(value) && value > 0) return Math.floor(value);
    if (typeof value === "string" && /^\d+$/.test(value.trim())) {
      const n = Number(value.trim());
      if (n > 0) return n;
    }
  }
  if (messages.length > 0) return messages.length;
  return 1;
}

function threadHasAttachmentsValue(thread: MailThread, messages: MailMessage[]): boolean {
  const raw = thread as MailThread & Record<string, unknown>;
  if (thread.hasAttachments) return true;
  if (raw.has_attachments === true || raw.hasAttachment === true) return true;
  if (typeof raw.attachmentCount === "number" && raw.attachmentCount > 0) return true;
  if (typeof raw.attachmentsCount === "number" && raw.attachmentsCount > 0) return true;
  if (Array.isArray(raw.attachments) && raw.attachments.length > 0) return true;
  return messages.some(
    (message) => Array.isArray(message.attachments) && message.attachments.length > 0,
  );
}

function threadHasAttachments(thread: MailThread): boolean {
  return threadHasAttachmentsValue(thread, thread.messages || []);
}

function truncateEmail(email?: string, max = 22) {
  if (!email) return "";
  if (email.length <= max) return email;
  return `${email.slice(0, max - 1)}…`;
}

function accountLabel(accounts: MailAccount[], accountId?: string) {
  if (!accountId) return "";
  const account = accounts.find((a) => a.id === accountId);
  if (!account) return "";
  return resolveAccountLabel(account);
}

function accountOptionLabel(account: MailAccount) {
  return resolveAccountLabel(account);
}

/**
 * Tip 210: reader stubs sometimes set message.id to the Gmail thread id (hub id suffix).
 * Using that in /messages/:id/attachments/... makes the API look up the wrong Gmail message.
 */
function mailMessageIdForAttachment(
  message: MailMessage | undefined,
  thread: MailThread | undefined,
  file: MailAttachment,
): string {
  const threadId = thread ? threadApiId(thread) : "";
  const fromOwner =
    (thread?.messages || []).find((row) =>
      (row.attachments || []).some(
        (part) => part.id && file.id && part.id === file.id,
      ),
    )?.id || "";
  const raw = String(fromOwner || message?.id || "").trim();
  if (!raw) return "";
  if (threadId && raw === threadId) return "";
  if (threadId.endsWith(`_${raw}`)) return "";
  return raw;
}

function mailActionPath(
  thread: Pick<MailThread, "id" | "threadId" | "accountId">,
  action: "read" | "unread" | "archive" | "delete" | "star" | "not-spam",
): string {
  const id = threadApiId(thread);
  const qs = thread.accountId ? `?accountId=${encodeURIComponent(thread.accountId)}` : "";
  return `/api/mail/${encodeURIComponent(id)}/${action}${qs}`;
}

/** List-row Gmail message ids so detail GET can hydrate messages missing from threads.get. */
function mailDetailQuery(thread: MailThread): string {
  const params = new URLSearchParams();
  if (thread.accountId) params.set("accountId", thread.accountId);
  const hintIds = (thread.messages || [])
    .map((message) => String(message.id || "").trim())
    .filter(
      (id) =>
        id &&
        !id.startsWith("msg_") &&
        !id.startsWith("gmail_") &&
        !id.startsWith("atw_"),
    );
  if (hintIds.length) params.set("messageIds", hintIds.join(","));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** Always produce at least one message so the reader has something to paint. */
function messagesFromThread(thread: MailThread): MailMessage[] {
  if (Array.isArray(thread.messages) && thread.messages.length) {
    return thread.messages.filter(Boolean).map((message) => {
      const raw = String(message.id || "");
      const stub =
        !raw || raw.startsWith("msg_") || raw.startsWith("gmail_") || raw.startsWith("atw_");
      return coerceMailMessage(stub ? { ...message, id: "" } : message, "");
    });
  }
  const id = "";
  // Do NOT copy snippet into bodyText — that made stubs look "hydrated" and
  // skipped GET /api/mail/:id (mashed single body, no chain count / paperclip).
  return [
    coerceMailMessage(
      {
        id,
        threadId: thread.threadId || thread.id,
        accountId: thread.accountId,
        subject: safeText(thread.subject) || undefined,
        snippet: safeText(thread.snippet) || undefined,
        fromName: formatMailContact(thread.fromName) || undefined,
        fromEmail: formatMailContact(thread.fromEmail) || undefined,
        from: formatMailContact(thread.from) || undefined,
        date: thread.date,
        unread: thread.unread,
        starred: thread.starred,
        attachments: [],
      },
      id,
    ),
  ];
}

function mergeHydratedMessage(
  existing: MailMessage[] | undefined,
  hydrated: MailMessage,
): MailMessage[] {
  const list = Array.isArray(existing) ? existing.filter(Boolean) : [];
  if (!list.length) return [hydrated];
  const hydratedId = String(hydrated.id || "");
  let replaced = false;
  const merged = list.map((message) => {
    if (hydratedId && String(message.id) === hydratedId) {
      replaced = true;
      return { ...message, ...hydrated };
    }
    return message;
  });
  if (!replaced) merged.push(hydrated);
  return merged;
}

/** Keep list-row messages (e.g. newest Gmail inbox hit) when detail GET omits them. */
function mergeDetailWithListThread(listThread: MailThread, detail: MailThread): MailThread {
  const listMessages = messagesFromThread(listThread);
  const detailMessages = messagesFromThread(detail);
  const byId = new Map<string, MailMessage>();
  for (const message of detailMessages) {
    const id = String(message.id || "").trim();
    if (id) byId.set(id, message);
  }
  for (const message of listMessages) {
    const id = String(message.id || "").trim();
    if (!id) continue;
    const existing = byId.get(id);
    if (!existing) {
      byId.set(id, message);
      continue;
    }
    const listBodies = extractMessageBodies(message);
    const detailBodies = extractMessageBodies(existing);
    const listScore =
      (listBodies.bodyHtml?.length || 0) +
      (listBodies.bodyText?.length || 0);
    const detailScore =
      (detailBodies.bodyHtml?.length || 0) +
      (detailBodies.bodyText?.length || 0);
    const pickDetail = detailScore >= listScore;
    byId.set(id, {
      ...message,
      ...existing,
      snippet: existing.snippet || message.snippet,
      bodyText: pickDetail ? detailBodies.bodyText || listBodies.bodyText : listBodies.bodyText || detailBodies.bodyText,
      bodyHtml: pickDetail ? detailBodies.bodyHtml || listBodies.bodyHtml : listBodies.bodyHtml || detailBodies.bodyHtml,
    });
  }
  const messages = [...byId.values()].sort((a, b) => {
    const da = messageDateValue(a) ? Date.parse(messageDateValue(a)!) : 0;
    const db = messageDateValue(b) ? Date.parse(messageDateValue(b)!) : 0;
    return da - db;
  });
  const latest = pickNewestMessage(messages);
  return coerceMailThread({
    ...listThread,
    ...detail,
    messages,
    fromName: latest?.fromName || detail.fromName || listThread.fromName,
    from: latest?.from || detail.from || listThread.from,
    date: latest?.date || detail.date || listThread.date,
  });
}

function normalizeDetailPayload(
  thread: MailThread,
  payload: MailThread | { thread?: MailThread; messages?: MailMessage[]; message?: MailMessage },
): MailThread {
  let next: MailThread;
  if (payload && typeof payload === "object" && "messages" in payload && Array.isArray(payload.messages)) {
    next = coerceMailThread({
      ...thread,
      ...payload,
      ...("thread" in payload && payload.thread ? payload.thread : {}),
      messages: payload.messages,
    });
  } else if (payload && typeof payload === "object" && "message" in payload && payload.message) {
    const message = payload.message;
    const nested =
      "thread" in payload && payload.thread && Array.isArray(payload.thread.messages)
        ? payload.thread.messages
        : thread.messages;
    next = coerceMailThread({
      ...thread,
      ...("thread" in payload && payload.thread ? payload.thread : {}),
      // Never collapse a multi-message chain to a single hydrated message.
      messages: mergeHydratedMessage(nested, message),
    });
  } else {
    const asThread = payload as MailThread;
    next = coerceMailThread({
      ...thread,
      ...asThread,
    });
  }
  const messages = next.messages || [];
  const ready =
    !next.bodyPending &&
    messages.length > 0 &&
    messages.every((message) => messageHasRealBody(message));
  return ready ? markThreadDetailExpanded(next) : { ...next, detailExpanded: false };
}

/** After a Gmail-style attribution line, treat following lines as quoted. */
function promoteWroteQuotes(text: string): string {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  let quoting = false;
  for (const line of lines) {
    if (/^On .+ wrote:\s*$/i.test(line.trim())) {
      out.push(line);
      quoting = true;
      continue;
    }
    if (quoting) {
      if (line.startsWith(">")) out.push(line);
      else out.push(line.length ? `> ${line}` : ">");
      continue;
    }
    out.push(line);
  }
  return out.join("\n");
}

/** First meaningful line for collapsed peeks — not the whole quote mash. */
function collapsedPeekLabel(message: MailMessage): string {
  const plain =
    (typeof message.bodyText === "string" && message.bodyText) ||
    (typeof message.bodyHtml === "string" && message.bodyHtml ? stripHtml(message.bodyHtml) : "") ||
    (typeof message.snippet === "string" && message.snippet) ||
    "";
  const line = plain
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((part) => part.trim())
    .find((part) => part && !/^on .+ wrote:$/i.test(part) && part !== ">");
  if (line) {
    return line.length > 120 ? `${line.slice(0, 117)}…` : line;
  }
  return "Collapsed — tap to show this email";
}

function safeBodyHtml(message: MailMessage): string {
  const html = typeof message.bodyHtml === "string" ? message.bodyHtml.trim() : "";
  const text = typeof message.bodyText === "string" ? message.bodyText : "";

  if (html && looksLikeHtml(html)) return html;

  const plainSource =
    text.includes("\n") || text.includes("\r")
      ? text
      : html
        ? stripHtml(html)
        : text;
  if (plainSource.includes("\n") || plainSource.includes("\r")) {
    // Hub sends sometimes drop leading `>` markers; re-quote after "On … wrote:".
    return plainToHtml(promoteWroteQuotes(plainSource));
  }

  if (html) return html;
  if (plainSource.trim()) {
    return `<pre>${escapeAndLinkify(plainSource)}</pre>`;
  }
  // Last resort: snippet is collapsed whitespace — still show something.
  const snippet = typeof message.snippet === "string" ? message.snippet.trim() : "";
  return snippet ? `<p>${escapeAndLinkify(snippet)}</p>` : "";
}

function attachmentLabel(file: MailAttachment, index: number): string {
  if (typeof file.filename === "string" && file.filename.trim()) return file.filename;
  return `attachment-${index + 1}`;
}

/** Stable key for opening spinner + click handlers (message id, else reader messageKey). */
function attachmentRowKey(
  file: MailAttachment,
  label: string,
  messageId: string | undefined,
  fallbackMessageKey: string,
): string {
  return `${messageId || fallbackMessageKey}::${file.id || label}`;
}

/** Long attachment lists collapse to this many items plus "+N more" (same idea as To/Cc chips). */
const COLLAPSED_ATTACHMENTS = 3;
const COLLAPSE_ATTACHMENTS_WHEN_OVER = 4;

function attachmentExtLabel(label: string): string {
  return (label.split(".").pop() || "FILE").slice(0, 4).toUpperCase();
}

/** Shared thumb / type tile; prefers a fetched blob URL so previews show real images (Tip 208). */
function AttachmentGlyph({
  file,
  label,
  thumbClassName,
  tileClassName,
  resolveThumbUrl,
}: {
  file: MailAttachment;
  label: string;
  thumbClassName: string;
  tileClassName: string;
  resolveThumbUrl?: () => Promise<string | null>;
}) {
  const immediate = attachmentOpenUrl(file);
  const [fetched, setFetched] = useState<string | null>(null);
  const [imgBroken, setImgBroken] = useState(false);

  useEffect(() => {
    setFetched(null);
    setImgBroken(false);
  }, [immediate, file.id, file.filename]);

  useEffect(() => {
    if (!isImageAttachment(file) || !resolveThumbUrl) return;
    let cancelled = false;
    void (async () => {
      try {
        const href = await resolveThumbUrl();
        if (!cancelled && href) setFetched(href);
      } catch {
        /* keep immediate fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
    // resolveThumbUrl is recreated each parent render — key off file identity only.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [file.id, file.filename, file.mimeType]);

  const src = fetched || immediate;
  useEffect(() => {
    setImgBroken(false);
  }, [src]);

  const showImg = Boolean(src) && isImageAttachment(file) && !imgBroken;

  if (showImg) {
    return (
      <img
        src={src}
        alt=""
        className={thumbClassName}
        onError={() => setImgBroken(true)}
      />
    );
  }
  return <span className={tileClassName}>{attachmentExtLabel(label)}</span>;
}

/** Horizontal pill preview (Tip 208): real image thumb + name; paperclip opens details. */
function MailAttachmentPreviewChip({
  file,
  label,
  opening,
  onOpen,
  resolveThumbUrl,
}: {
  file: MailAttachment;
  label: string;
  opening: boolean;
  onOpen: () => void;
  resolveThumbUrl?: () => Promise<string | null>;
}) {
  return (
    <button
      type="button"
      className={
        opening ? "attachment-preview-chip is-opening" : "attachment-preview-chip"
      }
      title={label}
      aria-label={opening ? `Opening ${label}` : `Open ${label}`}
      aria-busy={opening}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen();
      }}
    >
      <AttachmentGlyph
        file={file}
        label={label}
        thumbClassName="attachment-preview-thumb"
        tileClassName="attachment-preview-tile"
        resolveThumbUrl={resolveThumbUrl}
      />
      <span className="attachment-preview-name">{label}</span>
    </button>
  );
}

/** Detail row with filename (Tip 193). */
function MailAttachmentRow({
  file,
  label,
  opening,
  onOpen,
  resolveThumbUrl,
}: {
  file: MailAttachment;
  label: string;
  opening: boolean;
  onOpen: () => void;
  resolveThumbUrl?: () => Promise<string | null>;
}) {
  return (
    <button
      type="button"
      className={opening ? "attachment-row is-opening" : "attachment-row"}
      aria-busy={opening}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen();
      }}
    >
      <AttachmentGlyph
        file={file}
        label={label}
        thumbClassName="attachment-thumb"
        tileClassName="attachment-tile"
        resolveThumbUrl={resolveThumbUrl}
      />
      <span className="attachment-name">{label}</span>
      {opening ? <span className="attachment-opening muted">Opening…</span> : null}
    </button>
  );
}

function normalizeThreads(payload: MailListResponse | MailThread[]): MailThread[] {
  if (Array.isArray(payload)) return payload.map(coerceMailThread);
  if (Array.isArray(payload.threads)) {
    return payload.threads.map(coerceMailThread);
  }
  if (Array.isArray(payload.mail) && payload.mail.length) {
    return payload.mail.map(coerceMailThread);
  }
  if (Array.isArray(payload.messages) && payload.messages.length) {
    return payload.messages.map((message) =>
      coerceMailThread({
        id: message.threadId || message.id,
        threadId: message.threadId || message.id,
        accountId: message.accountId,
        subject: message.subject,
        snippet: message.snippet,
        fromName: message.fromName,
        fromEmail: message.fromEmail,
        from: message.from,
        date: message.date,
        unread: message.unread,
        starred: message.starred,
        messageCount: 1,
        hasAttachments: Boolean(message.attachments?.length),
        messages: [message],
      }),
    );
  }
  return [];
}

function coerceMailThread(thread: MailThread): MailThread {
  const messages = messagesFromThread(thread);
  const threadParticipants = (thread as MailThread & { participants?: unknown }).participants;
  const latest = pickNewestMessage(messages);
  // List payloads often omit thread-level from* and only put {name,email} on messages.
  const fromName =
    formatMailContact(thread.fromName) ||
    formatMailContact(latest?.fromName) ||
    formatMailContact(latest?.from) ||
    formatMailContact(messages[0]?.fromName) ||
    formatMailContact(messages[0]?.from) ||
    formatMailContact(thread.from) ||
    (Array.isArray(threadParticipants) ? formatMailContact(threadParticipants[0]) : "") ||
    undefined;
  const fromEmail =
    formatMailContact(thread.fromEmail) ||
    formatMailContact(latest?.fromEmail) ||
    formatMailContact(messages[0]?.fromEmail) ||
    undefined;
  const from =
    formatMailContact(thread.from) ||
    formatMailContact(latest?.from) ||
    formatMailContact(messages[0]?.from) ||
    fromName ||
    fromEmail ||
    undefined;
  const date = threadDateValue(thread, messages);
  const snippet = threadSnippetValue(thread, messages);
  const messageCount = threadMessageCountValue(thread, messages);
  const hasAttachments = threadHasAttachmentsValue(thread, messages);
  return {
    ...thread,
    subject: safeText(thread.subject) || undefined,
    snippet,
    fromName,
    fromEmail,
    from,
    date,
    messageCount,
    hasAttachments,
    messages,
  };
}

function usePhoneLayout() {
  const [phone, setPhone] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(PHONE_MQ).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(PHONE_MQ);
    const onChange = () => setPhone(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return phone;
}

function messageHasBody(message: MailMessage): boolean {
  if (messageHasRealBody(message)) return true;
  if (typeof message.snippet === "string" && message.snippet.trim()) return true;
  return false;
}

function sortedMessages(
  thread: MailThread | null | undefined,
  order: MailThreadOrderId = "newest-first",
): MailMessage[] {
  return orderMailThreadMessages(thread?.messages || [], order);
}

function mailErrorMessage(err: unknown, folder: string, account: string): string {
  if (err instanceof ApiError) {
    const details = err.details as {
      folder?: string;
      account?: string;
      failures?: Array<{
        message?: string;
        engineCode?: string;
        httpStatus?: number;
        ref?: string;
        retryAfter?: number;
        detail?: string;
      }>;
    } | undefined;
    const failure = details?.failures?.find((item) => item.message?.trim());
    const bits = [failure?.message?.trim() || err.message];
    const folderHint = details?.folder || folder;
    const accountHint = details?.account || account;
    if (folderHint) bits.push(`Folder: ${folderHint}`);
    if (accountHint) bits.push(`Account: ${accountHint}`);
    if (failure?.engineCode) {
      bits.push(`Code: ${failure.engineCode}${failure.httpStatus ? ` (HTTP ${failure.httpStatus})` : ""}`);
    }
    if (failure?.ref) bits.push(`Ref: ${failure.ref}`);
    if (failure?.retryAfter) bits.push(`Retry in ${failure.retryAfter}s`);
    if (failure?.detail) bits.push(`Detail: ${failure.detail.slice(0, 160)}`);
    return bits.join(" · ");
  }
  return err instanceof Error ? err.message : "Could not load mail.";
}

function sortThreads(
  threads: MailThread[],
  field: SortFieldId,
  dir: SortDirection,
): MailThread[] {
  const list = [...threads];
  const mul = dir === "asc" ? 1 : -1;
  list.sort((a, b) => {
    let cmp = 0;
    if (field === "from") {
      cmp = displayFrom(a).localeCompare(displayFrom(b));
    } else if (field === "subject") {
      cmp = (a.subject || "").localeCompare(b.subject || "");
    } else {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      cmp = da - db;
    }
    return cmp * mul;
  });
  return list;
}

function folderLabel(folder: MailFolderId): string {
  return MAIL_FOLDERS.find((item) => item.id === folder)?.label || "this folder";
}

function threadMatchesSearch(thread: MailThread, query: string): boolean {
  return matchesHubSearch(
    query,
    thread.subject,
    thread.snippet,
    thread.fromName,
    thread.fromEmail,
    thread.from,
    (thread as MailThread & { participants?: unknown }).participants,
    (thread.messages || []).map((message) => [
      message.subject,
      message.fromName,
      message.fromEmail,
      message.from,
      message.to,
      message.cc,
      message.snippet,
      message.bodyText,
    ]),
  );
}

function filterThreads(threads: MailThread[], filter: FilterId): MailThread[] {
  if (filter === "unread") return threads.filter((t) => t.unread);
  if (filter === "starred") return threads.filter((t) => t.starred);
  if (filter === "attachment") return threads.filter((t) => t.hasAttachments);
  return threads;
}

type ReaderChromeProps = {
  thread: MailThread;
  active: boolean;
  loading?: boolean;
  accounts: MailAccount[];
  folder: MailFolderId;
  threadOrder: MailThreadOrderId;
  clockFormat: MailClockFormatId;
  /** Tip 207: true = detail rows; unset/false = compact preview strip. */
  expandedAttachments: Record<string, boolean>;
  /** Tip 195: when many files, expand past the first few ("+N more"). */
  attachmentOverflowExpanded: Record<string, boolean>;
  collapsedBodies: Record<string, boolean>;
  moreMenuMessageId: string | null;
  onToggleAttachments: (messageKey: string) => void;
  onToggleAttachmentOverflow: (messageKey: string) => void;
  onToggleBody: (messageKey: string) => void;
  onOpenAttachment: (
    file: MailAttachment,
    message: MailMessage,
    thread: MailThread,
    messageKey: string,
  ) => void;
  openingAttachmentKey?: string | null;
  /** Resolve cid: / missing attachment URLs for inline images (tip 126). */
  onFetchAttachmentUrl: (
    file: MailAttachment,
    message: MailMessage,
    thread: MailThread,
  ) => Promise<string | null>;
  /** Tip 213: load .ics text when API did not already attach calendarInvite. */
  onFetchCalendarInviteText?: (
    file: MailAttachment,
    message: MailMessage,
    thread: MailThread,
  ) => Promise<string | null>;
  onStarThread: (thread: MailThread) => void;
  onCompose: (mode: ComposeMode, thread: MailThread, message: MailMessage) => void;
  onEditDraft: (thread: MailThread, message: MailMessage) => void;
  onDismissDraft: (thread: MailThread, message: MailMessage) => void;
  onToggleMoreMenu: (messageId: string | null) => void;
  onMarkUnread: (thread: MailThread) => void;
  onArchive: (thread: MailThread) => void;
  onNotSpam: (thread: MailThread) => void;
  onDelete: (thread: MailThread) => void;
  /** Phone reader: back arrow left of the subject. */
  onBack?: () => void;
  /** Seconds until the reader retries a rate-limited body. */
  bodyRetrySec?: number;
  onRetryBody?: () => void;
};

function messageAvatarInitial(message: MailMessage): string {
  const label = displayMailFrom(message) || "?";
  return label.trim().charAt(0).toUpperCase() || "?";
}

/** Compact provider id for the collapsed meta line. */
function shortMailId(id: string): string {
  const trimmed = id.trim();
  if (trimmed.length <= 16) return trimmed;
  return `${trimmed.slice(0, 8)}…${trimmed.slice(-4)}`;
}

async function copyMailText(text: string): Promise<boolean> {
  const value = text.trim();
  if (!value) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const area = document.createElement("textarea");
    area.value = value;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.left = "-9999px";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

function MailCopyIdControl({
  value,
  label,
  compact = false,
}: {
  value: string;
  label: string;
  compact?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  if (!value.trim()) return null;
  return (
    <button
      type="button"
      className={compact ? "mail-id-chip" : "mail-id-copy"}
      title={copied ? "Copied" : `Copy ${label}`}
      aria-label={copied ? `${label} copied` : `Copy ${label} ${value}`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void (async () => {
          const ok = await copyMailText(value);
          if (!ok) return;
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        })();
      }}
    >
      {compact ? (
        <>
          <span className="mail-id-chip-label">ID</span>
          <span className="mail-id-chip-value">{shortMailId(value)}</span>
          {copied ? <Check size={12} aria-hidden /> : null}
        </>
      ) : (
        <>
          <span className="mail-id-copy-value">{value}</span>
          <span className="mail-id-copy-action">{copied ? "Copied" : "Copy"}</span>
        </>
      )}
    </button>
  );
}

/** Reader meta: Month Day, Year, time — respects Profile 12h/24h. */
function formatReceivedDate(value?: string, clock: MailClockFormatId = "12h"): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: clock !== "24h",
  });
}

/** Prefer message date; fall back to provider alt fields / thread date. */
function messageDateValue(message: MailMessage, thread?: MailThread): string | undefined {
  const raw = message as MailMessage & Record<string, unknown>;
  return (
    coerceDateValue(message.date) ||
    coerceDateValue(raw.at) ||
    coerceDateValue(raw.receivedAt) ||
    coerceDateValue(raw.internalDate) ||
    coerceDateValue(raw.sentAt) ||
    coerceDateValue(raw.createdAt) ||
    (thread ? threadDateValue(thread, [message]) : undefined)
  );
}

function messageRecipientLine(message: MailMessage): string {
  return formatMailAddressListShort(message.to);
}

/** Full reader chrome for current + adjacent swipe peeks (never list-row stub). */
function MailThreadReader({
  thread,
  active,
  loading,
  accounts,
  folder,
  threadOrder,
  clockFormat,
  expandedAttachments,
  attachmentOverflowExpanded,
  collapsedBodies,
  moreMenuMessageId,
  onToggleAttachments,
  onToggleAttachmentOverflow,
  onToggleBody,
  onOpenAttachment,
  openingAttachmentKey = null,
  onFetchAttachmentUrl,
  onFetchCalendarInviteText,
  onStarThread,
  onCompose,
  onEditDraft,
  onDismissDraft,
  onToggleMoreMenu,
  onMarkUnread,
  onArchive,
  onNotSpam,
  onDelete,
  onBack,
  bodyRetrySec = 0,
  onRetryBody,
}: ReaderChromeProps) {
  const [expandedRecipients, setExpandedRecipients] = useState<Record<string, boolean>>({});
  // Reader always stacks the full chain. Conversations pref only groups the list.
  const accountEmail = threadAccountEmail(thread, accounts);
  const loaded = sortedMessages(thread, threadOrder);
  // Inbox stacks the whole conversation. Sent stacks only the mail we sent.
  const messages = messagesForMailFolder(loaded, folder, accountEmail);
  const chainTotal = messages.length;
  const hydrated = isMailThreadHydrated(thread);
  const chainGap = inboxChainGap(thread, folder, loaded.length);
  return (
    <div className="mail-reader" data-mail-swipe-reader={active ? "active" : "peek"} aria-hidden={!active}>
      <div className="mail-reader-chrome">
        <p className="mail-reader-meta muted">
          {displayFrom(thread)}
          {accountLabel(accounts, thread.accountId)
            ? ` · ${truncateEmail(accountLabel(accounts, thread.accountId), 28)}`
            : ""}
        </p>
        <div className="reader-subject-row">
          {onBack ? (
            <button
              type="button"
              className="phone-only mail-reader-back"
              aria-label="Back to inbox"
              title="Back to inbox"
              onClick={onBack}
            >
              <ChevronLeft size={22} aria-hidden />
            </button>
          ) : null}
          <h2 className="reader-subject-mobile">{thread.subject || "(no subject)"}</h2>
        </div>
      </div>
      {thread.bodyPending ? (
        <p className="banner-error mail-body-pending" role="status">
          Full message not loaded yet (
          {thread.bodyError?.code === "gmail_rate_limit"
            ? "Gmail is rate limiting"
            : "the mailbox is busy"}
          ).
          {bodyRetrySec > 0 ? ` Retrying in ${bodyRetrySec}s.` : ""}
          {onRetryBody ? (
            <button type="button" className="mail-body-retry" onClick={onRetryBody}>
              Retry
            </button>
          ) : null}
        </p>
      ) : null}
      {loading && !hydrated ? (
        <p className="muted pad mail-reader-loading">Loading message…</p>
      ) : null}
      {!loading && messages.length === 0 ? (
        <p className="muted pad">This thread has no message body yet.</p>
      ) : null}
      {/* Avoid empty identity/toolbar chrome while the chain is still hydrating. */}
      {loading && !hydrated
        ? null
        : messages.map((message, messageIndex) => {
        const files = Array.isArray(message.attachments) ? message.attachments : [];
        const messageKey = String(message.id || `msg-${messageIndex}`);
        // Tip 207: attachments always show as a compact preview; paperclip opens detail rows.
        // (Tip 193 used to hide the whole strip behind the paperclip.)
        const attachDetailsOpen = Boolean(expandedAttachments[messageKey]);
        // Tip 195: long lists collapse like To/Cc chips (first few + "+N more").
        const attachOverflowOpen = Boolean(attachmentOverflowExpanded[messageKey]);
        const attachCollapsed =
          !attachOverflowOpen && files.length > COLLAPSE_ATTACHMENTS_WHEN_OVER;
        const visibleFiles = attachCollapsed
          ? files.slice(0, COLLAPSED_ATTACHMENTS)
          : files;
        const hiddenAttachCount = files.length - visibleFiles.length;
        const bodyCollapsed = Boolean(collapsedBodies[messageKey]);
        const draft = isDraftMessage(message, folder);
        const sent = !draft && messageIsSent(message, accountEmail);
        const moreOpen = active && moreMenuMessageId === message.id;
        const recipientsExpanded = Boolean(expandedRecipients[messageKey]);
        const toLine = messageRecipientLine(message);
        const ccLine = messageCcLineShort(message);
        const bccLine = messageBccLineShort(message);
        const toLineFull = messageRecipientLineFull(message);
        const ccDetail = mailDetailCcDisplay(message);
        const bccDetail = mailDetailBccDisplay(message, sent || folder === "sent");
        const fromLineFull = formatMailFromFull(message);
        const received = formatReceivedDate(messageDateValue(message, thread), clockFormat);
        const chainOrdinal =
          chainTotal > 1 ? `${messageIndex + 1} of ${chainTotal}` : null;
        // Date · n of n (date omitted only when provider sent nothing parseable)
        const metaLine = [received, chainOrdinal].filter(Boolean).join(" · ");
        const subjectLine = safeText(message.subject) || safeText(thread.subject) || "";
        const bodyHtml = safeBodyHtml(message);
        const hasBody = messageHasBody(message);
        return (
          <article key={messageKey} className="mail-message">
            <div className="mail-people-row">
              <div className="mail-people-summary">
                <button
                  type="button"
                  className="mail-people-identity"
                  aria-expanded={recipientsExpanded}
                  aria-controls={`mail-details-${messageKey}`}
                  aria-label={
                    recipientsExpanded
                      ? "Hide message details"
                      : "Show message details with full email addresses"
                  }
                  title={recipientsExpanded ? "Hide details" : "Show details"}
                  onClick={() =>
                    setExpandedRecipients((prev) => ({
                      ...prev,
                      [messageKey]: !prev[messageKey],
                    }))
                  }
                >
                  <span className="mail-avatar" aria-hidden>
                    {messageAvatarInitial(message)}
                  </span>
                  <div className="mail-people-text">
                    <strong>
                      {displayFrom(message)}
                      {draft ? <span className="mail-draft-label"> draft</span> : null}
                      {sent ? <span className="mail-sent-label">Sent</span> : null}
                    </strong>
                    {toLine ? (
                      <p className="mail-people-to muted">
                        <span className="mail-people-to-label">To:</span>
                        <span className="mail-people-to-value">{toLine}</span>
                        <ChevronDown
                          size={14}
                          className={
                            recipientsExpanded
                              ? "mail-people-details-chevron open"
                              : "mail-people-details-chevron"
                          }
                          aria-hidden
                        />
                      </p>
                    ) : (
                      <p className="mail-people-to muted">
                        <span className="mail-people-to-label">Details</span>
                        <ChevronDown
                          size={14}
                          className={
                            recipientsExpanded
                              ? "mail-people-details-chevron open"
                              : "mail-people-details-chevron"
                          }
                          aria-hidden
                        />
                      </p>
                    )}
                    {/* Tip 121: always show Cc when present — tip 092 hid it until expand,
                        so Kyle thought Sheri was dropped from the chain after send. */}
                    {ccLine && !recipientsExpanded ? (
                      <p className="mail-people-to muted">
                        <span className="mail-people-to-label">Cc:</span>
                        <span className="mail-people-to-value">{ccLine}</span>
                      </p>
                    ) : null}
                    {bccLine && (sent || folder === "sent") && !recipientsExpanded ? (
                      <p className="mail-people-to muted">
                        <span className="mail-people-to-label">Bcc:</span>
                        <span className="mail-people-to-value">{bccLine}</span>
                      </p>
                    ) : null}
                    {!recipientsExpanded && (metaLine || message.id) ? (
                      <p className="mail-people-meta muted">
                        {metaLine ? <span>{metaLine}</span> : null}
                        {metaLine && message.id ? <span aria-hidden> · </span> : null}
                        {message.id ? (
                          <MailCopyIdControl value={message.id} label="ID" compact />
                        ) : null}
                      </p>
                    ) : null}
                  </div>
                </button>
                {recipientsExpanded ? (
                  <div
                    id={`mail-details-${messageKey}`}
                    className="mail-message-details"
                    role="region"
                    aria-label="Message details"
                  >
                    <dl className="mail-message-details-list">
                      {fromLineFull ? (
                        <div className="mail-message-details-row">
                          <dt>from</dt>
                          <dd>{fromLineFull}</dd>
                        </div>
                      ) : null}
                      {toLineFull ? (
                        <div className="mail-message-details-row">
                          <dt>to</dt>
                          <dd>{toLineFull}</dd>
                        </div>
                      ) : null}
                      <div className="mail-message-details-row">
                        <dt>cc</dt>
                        <dd>{ccDetail}</dd>
                      </div>
                      {bccDetail !== null ? (
                        <div className="mail-message-details-row">
                          <dt>bcc</dt>
                          <dd>{bccDetail}</dd>
                        </div>
                      ) : null}
                      {received ? (
                        <div className="mail-message-details-row">
                          <dt>date</dt>
                          <dd>{received}</dd>
                        </div>
                      ) : null}
                      {subjectLine ? (
                        <div className="mail-message-details-row">
                          <dt>subject</dt>
                          <dd>{subjectLine}</dd>
                        </div>
                      ) : null}
                      {message.id ? (
                        <div className="mail-message-details-row">
                          <dt>id</dt>
                          <dd>
                            <MailCopyIdControl value={message.id} label="ID" />
                          </dd>
                        </div>
                      ) : null}
                      {message.messageIdHeader ? (
                        <div className="mail-message-details-row">
                          <dt>message-id</dt>
                          <dd>
                            <MailCopyIdControl
                              value={message.messageIdHeader}
                              label="Message-ID"
                            />
                          </dd>
                        </div>
                      ) : null}
                    </dl>
                  </div>
                ) : null}
              </div>
              {active ? (
                <div className="mail-people-actions">
                  {files.length > 0 ? (
                    <button
                      type="button"
                      className={
                        attachDetailsOpen
                          ? "icon-btn mail-attach-toggle is-details"
                          : "icon-btn mail-attach-toggle"
                      }
                      aria-pressed={attachDetailsOpen}
                      aria-label={
                        attachDetailsOpen
                          ? `Show ${files.length} attachment preview${files.length === 1 ? "" : "s"}`
                          : `Show ${files.length} attachment detail${files.length === 1 ? "" : "s"}`
                      }
                      title={
                        attachDetailsOpen
                          ? `Show attachment previews (${files.length})`
                          : `Show attachment details (${files.length})`
                      }
                      onClick={() => onToggleAttachments(messageKey)}
                    >
                      <Paperclip size={18} />
                      <span className="mail-attach-count" aria-hidden>
                        {files.length}
                      </span>
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={bodyCollapsed ? "Expand message" : "Collapse message"}
                    title={bodyCollapsed ? "Expand message" : "Collapse message"}
                    onClick={() => onToggleBody(messageKey)}
                  >
                    {bodyCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                  </button>
                </div>
              ) : null}
            </div>

            {active ? (
              <div
                className="mail-message-actions"
                role="toolbar"
                aria-label={draft ? "Draft actions" : "Message actions"}
              >
                <button
                  type="button"
                  className={thread.starred ? "mail-icon-btn active" : "mail-icon-btn"}
                  title={thread.starred ? "Starred" : "Star"}
                  aria-label={thread.starred ? "Starred" : "Star"}
                  onClick={() => onStarThread(thread)}
                >
                  <Star size={18} fill={thread.starred ? "currentColor" : "none"} />
                </button>
                {draft ? (
                  <>
                    <button
                      type="button"
                      className="mail-icon-btn"
                      title="Edit Draft"
                      aria-label="Edit Draft"
                      onClick={() => onEditDraft(thread, message)}
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      type="button"
                      className="mail-icon-btn"
                      title="Dismiss Draft"
                      aria-label="Dismiss Draft"
                      onClick={() => onDismissDraft(thread, message)}
                    >
                      <Trash2 size={18} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="mail-icon-btn"
                      title="Reply"
                      aria-label="Reply"
                      onClick={() => onCompose("reply", thread, message)}
                    >
                      <Reply size={18} />
                    </button>
                    <button
                      type="button"
                      className="mail-icon-btn"
                      title="Reply All"
                      aria-label="Reply All"
                      onClick={() => onCompose("replyAll", thread, message)}
                    >
                      <ReplyAll size={18} />
                    </button>
                    <button
                      type="button"
                      className="mail-icon-btn"
                      title="Forward"
                      aria-label="Forward"
                      onClick={() => onCompose("forward", thread, message)}
                    >
                      <Forward size={18} />
                    </button>
                    {folder === "junk" ? (
                      <button
                        type="button"
                        className="mail-text-btn"
                        title="Not Spam: move to Inbox and train the spam filter"
                        aria-label="Not Spam"
                        onClick={() => onNotSpam(thread)}
                      >
                        <Inbox size={16} aria-hidden />
                        Not Spam
                      </button>
                    ) : null}
                    <div className="mail-more-wrap">
                      <button
                        type="button"
                        className={moreOpen ? "mail-icon-btn active" : "mail-icon-btn"}
                        title="More Actions"
                        aria-label="More Actions"
                        aria-expanded={moreOpen}
                        onClick={() => onToggleMoreMenu(moreOpen ? null : message.id)}
                      >
                        <MoreHorizontal size={18} />
                      </button>
                      {moreOpen ? (
                        <div className="mail-more-menu" role="menu">
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              onToggleMoreMenu(null);
                              onMarkUnread(thread);
                            }}
                          >
                            Mark Unread
                          </button>
                          {folder === "junk" ? (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                onToggleMoreMenu(null);
                                onNotSpam(thread);
                              }}
                            >
                              Not Spam
                            </button>
                          ) : null}
                          {folder !== "archive" && folder !== "junk" ? (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                onToggleMoreMenu(null);
                                onArchive(thread);
                              }}
                            >
                              Archive
                            </button>
                          ) : null}
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              onToggleMoreMenu(null);
                              onDelete(thread);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            ) : null}

            {active && files.length > 0 ? (
              attachDetailsOpen ? (
                <ul className="attachment-list">
                {visibleFiles.map((file, index) => {
                  const label = attachmentLabel(file, index);
                  const rowKey = attachmentRowKey(
                    file,
                    label,
                    message.id,
                    messageKey,
                  );
                  const opening = openingAttachmentKey === rowKey;
                  return (
                    <li key={`${label}-${index}`}>
                      <MailAttachmentRow
                        file={file}
                        label={label}
                        opening={opening}
                        onOpen={() => onOpenAttachment(file, message, thread, messageKey)}
                        resolveThumbUrl={
                          isImageAttachment(file)
                            ? () => onFetchAttachmentUrl(file, message, thread)
                            : undefined
                        }
                      />
                    </li>
                  );
                })}
                {attachCollapsed ? (
                  <li className="attachment-list-actions">
                    <button
                      type="button"
                      className="attachment-list-more"
                      title={`Show all ${files.length} attachments`}
                      aria-label={`Show ${hiddenAttachCount} more attachments`}
                      onClick={() => onToggleAttachmentOverflow(messageKey)}
                    >
                      +{hiddenAttachCount} more
                    </button>
                  </li>
                ) : null}
                {attachOverflowOpen && files.length > COLLAPSE_ATTACHMENTS_WHEN_OVER ? (
                  <li className="attachment-list-actions">
                    <button
                      type="button"
                      className="attachment-list-more"
                      title="Show fewer attachments"
                      aria-label="Show fewer attachments"
                      onClick={() => onToggleAttachmentOverflow(messageKey)}
                    >
                      Show less
                    </button>
                  </li>
                ) : null}
              </ul>
              ) : (
                <ul className="attachment-preview-strip" aria-label="Attachment previews">
                  {visibleFiles.map((file, index) => {
                    const label = attachmentLabel(file, index);
                    const rowKey = attachmentRowKey(
                      file,
                      label,
                      message.id,
                      messageKey,
                    );
                    const opening = openingAttachmentKey === rowKey;
                    return (
                      <li key={`${label}-${index}`}>
                        <MailAttachmentPreviewChip
                          file={file}
                          label={label}
                          opening={opening}
                          onOpen={() =>
                            onOpenAttachment(file, message, thread, messageKey)
                          }
                          resolveThumbUrl={
                            isImageAttachment(file)
                              ? () => onFetchAttachmentUrl(file, message, thread)
                              : undefined
                          }
                        />
                      </li>
                    );
                  })}
                  {attachCollapsed ? (
                    <li className="attachment-preview-more-wrap">
                      <button
                        type="button"
                        className="attachment-preview-more"
                        title={`Show all ${files.length} attachments`}
                        aria-label={`Show ${hiddenAttachCount} more attachments`}
                        onClick={() => onToggleAttachmentOverflow(messageKey)}
                      >
                        +{hiddenAttachCount}
                      </button>
                    </li>
                  ) : null}
                  {attachOverflowOpen && files.length > COLLAPSE_ATTACHMENTS_WHEN_OVER ? (
                    <li className="attachment-preview-more-wrap">
                      <button
                        type="button"
                        className="attachment-preview-more"
                        title="Show fewer attachments"
                        aria-label="Show fewer attachments"
                        onClick={() => onToggleAttachmentOverflow(messageKey)}
                      >
                        −
                      </button>
                    </li>
                  ) : null}
                </ul>
              )
            ) : null}

            {!bodyCollapsed ? (
              hydrated || !loading ? (
                <>
                  {active &&
                  (message.calendarInvite || files.some((file) => isCalendarAttachment(file))) &&
                  accountEmail &&
                  thread.accountId ? (
                    <MailInviteRsvpCard
                      message={message}
                      accountId={thread.accountId}
                      accountEmail={accountEmail}
                      fetchAttachmentText={
                        onFetchCalendarInviteText
                          ? (file) => onFetchCalendarInviteText(file, message, thread)
                          : undefined
                      }
                    />
                  ) : null}
                  {hasBody ? (
                    <MailBodyHtml
                      html={bodyHtml}
                      attachments={files}
                      fetchAttachmentUrl={(file) =>
                        onFetchAttachmentUrl(file, message, thread)
                      }
                    />
                  ) : message.calendarInvite ||
                    files.some((file) => isCalendarAttachment(file)) ? null : (
                    <p className="mail-body-empty muted">This message has no body.</p>
                  )}
                </>
              ) : null
            ) : (
              <button
                type="button"
                className="mail-body-collapsed-peek"
                onClick={() => onToggleBody(messageKey)}
              >
                <span className="muted">{collapsedPeekLabel(message)}</span>
              </button>
            )}
          </article>
        );
      })}
      {chainGap ? (
        <p className="muted pad mail-chain-incomplete">
          Showing {chainGap.showing} of {chainGap.total} emails in this chain.
          {loading ? " Loading the rest…" : ""}
        </p>
      ) : null}
    </div>
  );
}

export function MailPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlMail = useMemo(() => parseMailSearchParams(searchParams), [searchParams]);
  const boot = typeof window !== "undefined" ? readMailBootstrap() : null;
  const nav = typeof window !== "undefined" ? readMailNav() : null;
  const initialFolder = normalizeMailFolderId(
    urlMail?.folder || nav?.folder || boot?.folder || "inbox",
  ) as MailFolderId;
  const initialAccount = urlMail?.account || nav?.account || boot?.account || "all";
  const bootMatches =
    Boolean(boot?.threads?.length) &&
    normalizeMailFolderId(boot?.folder || "inbox") === initialFolder &&
    (boot?.account || "all") === initialAccount;

  const [accounts, setAccounts] = useState<MailAccount[]>(() => boot?.accounts || []);
  const [folder, setFolder] = useState<MailFolderId>(initialFolder);
  const [accountFilter, setAccountFilter] = useState<string>(initialAccount);
  const [providerFolders, setProviderFolders] = useState<Record<string, MailProviderFolder[]>>({});
  const [folderSyncTick, setFolderSyncTick] = useState(0);
  const [sortField, setSortField] = useState<SortFieldId>("date");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [filter, setFilter] = useState<FilterId>("all");
  const [sortOpen, setSortOpen] = useState(false);

  function selectSortField(field: SortFieldId) {
    if (field === sortField) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
      return;
    }
    setSortField(field);
    // Keep Date on newest-first when returning to it; alpha fields default A→Z.
    setSortDir(field === "date" ? "desc" : "asc");
  }
  const [threads, setThreads] = useState<MailThread[]>(() =>
    bootMatches ? filterThreadsForAccountView(boot!.threads, initialAccount) : [],
  );
  const searchQuery = useHubSearchQuery("mail").trim();
  const searchTerm = useDebouncedValue(searchQuery, 400);
  const [searchResult, setSearchResult] = useState<{
    key: string;
    threads: MailThread[];
    /** The API ran the search itself (provider search can match text the list rows don't carry). */
    serverMatched: boolean;
  } | null>(null);
  const [searchError, setSearchError] = useState("");
  /** Row changes (read, star, archive, delete) must reach search hits that aren't in the folder list. */
  const updateListedThreads = useCallback((change: (list: MailThread[]) => MailThread[]) => {
    setThreads(change);
    setSearchResult((result) => (result ? { ...result, threads: change(result.threads) } : result));
  }, []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MailThread | null>(null);
  // Skip the blank spinner when we already painted from local storage.
  const [loading, setLoading] = useState(() => !bootMatches);
  const [listRefreshing, setListRefreshing] = useState(() => bootMatches);
  const [pullPx, setPullPx] = useState(0);
  const [refreshNotice, setRefreshNotice] = useState("");
  const [listPageRateBanner, setListPageRateBanner] = useState("");
  const refreshNoticeTimer = useRef<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [composeDraft, setComposeDraft] = useState<ComposeDraft | null>(null);
  const [moreMenuMessageId, setMoreMenuMessageId] = useState<string | null>(null);
  const [emailsDrawerOpen, setEmailsDrawerOpen] = useState(false);
  const [treeExpanded, setTreeExpanded] = useState<Record<string, boolean>>({ all: true });
  const [expandedAttachments, setExpandedAttachments] = useState<Record<string, boolean>>({});
  const [attachmentOverflowExpanded, setAttachmentOverflowExpanded] = useState<
    Record<string, boolean>
  >({});
  const [openingAttachmentKey, setOpeningAttachmentKey] = useState<string | null>(null);
  const [attachPreview, setAttachPreview] = useState<{
    items: { href: string; label: string; mimeType: string }[];
    index: number;
  } | null>(null);
  /** Bumps when opening a preview so late sibling fetches cannot clobber a newer one. */
  const attachPreviewGenRef = useRef(0);
  const [collapsedBodies, setCollapsedBodies] = useState<Record<string, boolean>>({});
  const [mailView, setMailView] = useState<MailViewId>(() => readMailView());
  const [conversations, setConversations] = useState(() => readMailConversations());
  const [threadOrder, setThreadOrder] = useState<MailThreadOrderId>(() => readMailThreadOrder());
  const [swipeLeft, setSwipeLeft] = useState<MailSwipeActionId>(() => readMailSwipeLeft());
  const [swipeRight, setSwipeRight] = useState<MailSwipeActionId>(() => readMailSwipeRight());
  const [clockFormat, setClockFormat] = useState<MailClockFormatId>(() => readMailClockFormat());
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try {
      return localStorage.getItem(NAV_COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [labelTick, setLabelTick] = useState(0);
  const phoneLayout = usePhoneLayout();
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeDragging, setSwipeDragging] = useState(false);
  const [cacheTick, setCacheTick] = useState(0);
  const [prefetchingKeys, setPrefetchingKeys] = useState<Record<string, true>>({});
  const detailCacheRef = useRef<Map<string, MailThread>>(new Map());
  const quotaPauseRef = useRef<{ until: number; accountId: string } | null>(null);
  const bodyAutoRetriedRef = useRef("");
  const lastFocusRefreshRef = useRef(0);
  const accountFilterRef = useRef(accountFilter);
  accountFilterRef.current = accountFilter;
  const [bodyRetryNow, setBodyRetryNow] = useState(() => Date.now());
  /** True once bootstrap/IDB/network has put rows on screen (avoids loadList↔threads loops). */
  const hasPaintedListRef = useRef(bootMatches);
  const listPaneRef = useRef<HTMLElement | null>(null);
  /** Reader scroll container — reset on thread change so prior scrollTop does not hide chrome. */
  const mailReaderPaneRef = useRef<HTMLElement | null>(null);
  const pullStartY = useRef<number | null>(null);
  const pullArmed = useRef(false);

  function dismissRefreshNotice() {
    if (refreshNoticeTimer.current != null) {
      window.clearTimeout(refreshNoticeTimer.current);
      refreshNoticeTimer.current = null;
    }
    setRefreshNotice("");
  }
  const swipeStageRef = useRef<HTMLDivElement | null>(null);
  const swipeStartX = useRef<number | null>(null);
  const swipeStartY = useRef<number | null>(null);
  const swipeAxis = useRef<"x" | "y" | null>(null);
  const swipeWidth = useRef(320);
  const swipeCommitTimer = useRef<number | null>(null);
  const prefetchInflight = useRef<Set<string>>(new Set());
  /** Local sends waiting for the provider chain to catch up — keyed by threadRowKey. */
  const pendingSentRef = useRef<Map<string, MailMessage[]>>(new Map());
  /** Last successful GET /api/mail/:id per row — skip only while fresh. */
  const detailFetchedAtRef = useRef<Map<string, number>>(new Map());
  const selectedIdRef = useRef<string | null>(null);
  const openThreadRef = useRef<
    ((thread: MailThread, opts?: { backgroundRefresh?: boolean; force?: boolean }) => Promise<void>) | null
  >(null);
  const loadListRef = useRef<((opts?: { soft?: boolean; quiet?: boolean }) => Promise<void>) | null>(null);
  const quietListInflightRef = useRef(false);
  /** Bumps on every list fetch so a slow All Inboxes response cannot overwrite Marks Group. */
  const listRequestIdRef = useRef(0);
  const windowSyncStartedRef = useRef(false);
  const windowSyncUpdatedRef = useRef<((folder: string, accountId: string) => void) | null>(null);
  const mountedRef = useRef(true);
  const DETAIL_FRESH_MS = 12_000;
  /** Deep link id from URL — open once list (or accounts) is ready. */
  const pendingUrlOpenRef = useRef<string | null>(urlMail?.id || null);
  /** Avoid stripping ?id= from the URL before the deep link runs. */
  const urlSyncReadyRef = useRef(!urlMail?.id);
  const urlOpenAttemptedRef = useRef(false);

  function rememberPendingSent(rowKey: string, message: MailMessage) {
    if (!rowKey) return;
    const list = pendingSentRef.current.get(rowKey) || [];
    pendingSentRef.current.set(rowKey, [
      message,
      ...list.filter((entry) => entry.id !== message.id),
    ]);
  }

  function mergePendingSent(thread: MailThread): MailThread {
    const key = threadRowKey(thread);
    const pending = pendingSentRef.current.get(key);
    if (!pending?.length) return thread;
    const existing = Array.isArray(thread.messages) ? thread.messages : [];
    const collapse = (value: string) => value.replace(/\s+/g, " ").trim();
    const keep: MailMessage[] = [];
    for (const local of pending) {
      const localText = collapse((local.bodyText || local.snippet || "").slice(0, 160));
      const matched = existing.some((message) => {
        if (local.id && message.id && String(message.id) === String(local.id)) return true;
        const remoteText = collapse((message.bodyText || message.snippet || "").slice(0, 160));
        if (localText && remoteText && localText === remoteText) return true;
        const localFrom = (local.fromEmail || "").toLowerCase();
        const remoteFrom = (
          formatMailContact(message.fromEmail) ||
          formatMailContact(message.from) ||
          ""
        ).toLowerCase();
        if (
          localText &&
          remoteText &&
          localFrom &&
          remoteFrom &&
          localFrom === remoteFrom &&
          remoteText.includes(localText.slice(0, Math.min(40, localText.length)))
        ) {
          return true;
        }
        return false;
      });
      if (!matched) keep.push(local);
    }
    // Drop resolved locals from the pending map.
    const stillPending = pending.filter((local) => keep.some((entry) => entry.id === local.id));
    if (stillPending.length) pendingSentRef.current.set(key, stillPending);
    else pendingSentRef.current.delete(key);
    if (!keep.length) return thread;
    return markThreadDetailExpanded({
      ...thread,
      messages: [...keep, ...existing],
      messageCount: Math.max(thread.messageCount || 0, existing.length) + keep.length,
      snippet: keep[0]?.snippet || thread.snippet,
    });
  }

  function putDetailCache(thread: MailThread) {
    const key = threadRowKey(thread);
    if (!key || key.endsWith("::")) return;
    if (!shouldCacheMailDetail(thread)) return;
    detailCacheRef.current.set(key, thread);
    setCacheTick((tick) => tick + 1);
    void writeCachedMailDetail(key, thread);
    // List payloads often omit / under-count messageCount. When detail (or
    // prefetch) hydrates the real chain, push count + paperclip onto the inbox
    // row and IDB immediately — do not wait for a later list refresh.
    const messages = thread.messages || [];
    const email = threadAccountEmail(thread, accounts);
    const visible = messagesForMailFolder(messages, folder, email);
    const authoritative = Boolean(thread.detailExpanded);
    const count =
      folder === "sent" || folder === "drafts"
        ? Math.max(visible.length, 1)
        : authoritative
          ? Math.max(visible.length, 1)
          : Math.max(
              threadMessageCountValue(thread, messages),
              visible.length,
              typeof thread.messageCount === "number" ? thread.messageCount : 0,
            );
    const hasAttach = threadHasAttachmentsValue(thread, messages);
    // A one-message chain used to skip the list update. Sent still has to
    // correct a badge that counted the whole conversation.
    if (count <= 1 && !hasAttach && !authoritative && folder !== "sent" && folder !== "drafts") {
      return;
    }
    setThreads((list) => {
      let changed = false;
      const next = list.map((row) => {
        if (threadRowKey(row) !== key) return row;
        const prevCount = typeof row.messageCount === "number" ? row.messageCount : 1;
        const nextCount =
          folder === "sent" || folder === "drafts" || authoritative ? count : Math.max(prevCount, count);
        const nextAttach = Boolean(row.hasAttachments) || hasAttach;
        if (nextCount === prevCount && nextAttach === Boolean(row.hasAttachments)) return row;
        changed = true;
        return { ...row, messageCount: nextCount, hasAttachments: nextAttach };
      });
      if (changed) {
        void writeCachedMailList(
          { account: accountFilter || "all", folder },
          next,
          accounts,
        );
      }
      return changed ? next : list;
    });
  }

  function resolveCachedThread(listThread: MailThread | null | undefined): MailThread | null {
    if (!listThread) return null;
    const key = threadRowKey(listThread);
    const cached = detailCacheRef.current.get(key);
    if (cached) return cached;
    return coerceMailThread(listThread);
  }

  useEffect(() => {
    function openDrawer() {
      setEmailsDrawerOpen(true);
    }
    function openCompose() {
      const accountId = accounts[0]?.id || "";
      setComposeDraft(emptyComposeDraft(accountId));
    }
    window.addEventListener("atw-hub-open-mail-drawer", openDrawer);
    window.addEventListener("atw-hub-new-message", openCompose);
    return () => {
      window.removeEventListener("atw-hub-open-mail-drawer", openDrawer);
      window.removeEventListener("atw-hub-new-message", openCompose);
    };
  }, [accounts]);

  // Hide phone FAB while compose is open (FAB z-index sits above in-reader compose).
  useEffect(() => {
    const open = Boolean(composeDraft);
    document.documentElement.dataset.compose = open ? "1" : "";
    window.dispatchEvent(
      new CustomEvent(open ? "atw-hub-compose-open" : "atw-hub-compose-close"),
    );
    return () => {
      document.documentElement.dataset.compose = "";
      window.dispatchEvent(new CustomEvent("atw-hub-compose-close"));
    };
  }, [composeDraft]);

  // Hide FAB while reading mail — FAB (z-index 46) sits above the reader (35) and
  // steals touches at the bottom, which made upward scroll feel stuck after tip 117.
  useEffect(() => {
    const reading = Boolean(selectedId) && !composeDraft;
    if (reading) document.documentElement.dataset.mailReader = "1";
    else delete document.documentElement.dataset.mailReader;
    return () => {
      delete document.documentElement.dataset.mailReader;
    };
  }, [selectedId, composeDraft]);

  useEffect(() => {
    function syncView() {
      setMailView(readMailView());
    }
    function syncConversations() {
      setConversations(readMailConversations());
    }
    function syncThreadOrder() {
      setThreadOrder(readMailThreadOrder());
    }
    function syncSwipe() {
      setSwipeLeft(readMailSwipeLeft());
      setSwipeRight(readMailSwipeRight());
    }
    function syncClock() {
      setClockFormat(readMailClockFormat());
    }
    window.addEventListener(MAIL_VIEW_EVENT, syncView);
    window.addEventListener(MAIL_CONVERSATIONS_EVENT, syncConversations);
    window.addEventListener(MAIL_THREAD_ORDER_EVENT, syncThreadOrder);
    window.addEventListener(MAIL_SWIPE_EVENT, syncSwipe);
    window.addEventListener(MAIL_CLOCK_FORMAT_EVENT, syncClock);
    window.addEventListener("storage", syncView);
    window.addEventListener("storage", syncConversations);
    window.addEventListener("storage", syncThreadOrder);
    window.addEventListener("storage", syncSwipe);
    window.addEventListener("storage", syncClock);
    return () => {
      window.removeEventListener(MAIL_VIEW_EVENT, syncView);
      window.removeEventListener(MAIL_CONVERSATIONS_EVENT, syncConversations);
      window.removeEventListener(MAIL_THREAD_ORDER_EVENT, syncThreadOrder);
      window.removeEventListener(MAIL_SWIPE_EVENT, syncSwipe);
      window.removeEventListener(MAIL_CLOCK_FORMAT_EVENT, syncClock);
      window.removeEventListener("storage", syncView);
      window.removeEventListener("storage", syncConversations);
      window.removeEventListener("storage", syncThreadOrder);
      window.removeEventListener("storage", syncSwipe);
      window.removeEventListener("storage", syncClock);
    };
  }, []);

  function toggleTreeBranch(id: string) {
    setTreeExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function selectAccountFolder(nextAccount: string, nextFolder: MailFolderId) {
    setAccountFilter(nextAccount);
    setFolder(nextFolder);
    setSelectedId(null);
    setDetail(null);
    setEmailsDrawerOpen(false);
  }

  useEffect(() => {
    function onSyncChange() {
      setFolderSyncTick((tick) => tick + 1);
    }
    window.addEventListener(MAIL_SYNC_FOLDERS_EVENT, onSyncChange);
    window.addEventListener("storage", onSyncChange);
    return () => {
      window.removeEventListener(MAIL_SYNC_FOLDERS_EVENT, onSyncChange);
      window.removeEventListener("storage", onSyncChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadProviderFolders() {
      const syncMap = readFolderSyncMap();
      const enabled = accounts.filter((account) => syncMap[account.id]);
      if (!enabled.length) {
        if (!cancelled) setProviderFolders({});
        return;
      }
      const entries = await Promise.all(
        enabled.map(async (account) => {
          const folders = await fetchAccountFolders(account.id);
          return [account.id, folders] as const;
        }),
      );
      if (cancelled) return;
      const next: Record<string, MailProviderFolder[]> = {};
      for (const [id, folders] of entries) next[id] = folders;
      setProviderFolders(next);
    }
    void loadProviderFolders();
    return () => {
      cancelled = true;
    };
  }, [accounts, folderSyncTick]);

  useEffect(() => {
    const navAccount =
      accountFilter === "all" && accounts.length === 1 ? accounts[0]!.id : accountFilter;
    const nav = foldersForAccountNav(navAccount, providerFolders);
    if (!nav.some((item) => item.id === folder)) {
      setFolder("inbox");
    }
  }, [accountFilter, accounts, folder, providerFolders, folderSyncTick]);

  function providerLabel(account: MailAccount) {
    if (account.providerId) {
      const id = account.providerId.toLowerCase();
      if (id.includes("google") || id.includes("gmail")) return "Google";
      if (id.includes("outlook") || id.includes("microsoft")) return "Outlook";
      if (id.includes("zoho")) return "Zoho";
      if (id.includes("yahoo")) return "Yahoo";
      if (id.includes("atw")) return "ATW Mail";
    }
    const email = (account.email || "").toLowerCase();
    if (email.endsWith("@gmail.com") || email.includes("google")) return "Google";
    if (email.includes("outlook") || email.includes("hotmail")) return "Outlook";
    if (email.includes("zoho")) return "Zoho";
    if (email.includes("yahoo")) return "Yahoo";
    return "Mail";
  }

  const loadList = useCallback(async (opts?: { soft?: boolean; quiet?: boolean }) => {
    const quiet = Boolean(opts?.quiet);
    const soft = Boolean(opts?.soft) || quiet;
    const viewAccount = accountFilter || "all";
    const cacheKey = { account: viewAccount, folder };
    const requestId = ++listRequestIdRef.current;
    const stillCurrent = () => requestId === listRequestIdRef.current && mountedRef.current;
    if (quiet && quietListInflightRef.current) return;
    if (quiet) quietListInflightRef.current = true;
    else setError("");
    let completed = false;
    let paintedFromCache = false;

    // Local store first: sync bootstrap already painted on mount; IDB fills gaps.
    if (quiet) {
      // New-mail check: no spinner, toast, or error banner.
    } else if (!soft) {
      const cachedAccounts = await readCachedMailAccounts();
      if (!stillCurrent()) return;
      if (cachedAccounts?.length) setAccounts(cachedAccounts);
      const cached = await readCachedMailList(cacheKey);
      if (!stillCurrent()) return;
      if (cached?.length) {
        setThreads(cached);
        hasPaintedListRef.current = true;
        paintedFromCache = true;
        setLoading(false);
        setListRefreshing(true);
      } else if (hasPaintedListRef.current) {
        // Bootstrap already on screen — background refresh only.
        paintedFromCache = true;
        setLoading(false);
        setListRefreshing(true);
      } else {
        setLoading(true);
      }
    } else {
      setListRefreshing(true);
    }

    try {
      const accountPayload = await api<{ accounts?: MailAccount[] } | MailAccount[]>(
        "/api/mail/accounts",
        { timeoutMs: MAIL_ACCOUNTS_TIMEOUT_MS },
      ).catch(() => ({ accounts: [] as MailAccount[] }));
      if (!stillCurrent()) return;
      const nextAccounts = Array.isArray(accountPayload)
        ? accountPayload
        : accountPayload.accounts || [];
      applyServerAccountLabels(nextAccounts);
      setAccounts(nextAccounts);
      void writeCachedMailAccounts(nextAccounts);

      if (nextAccounts.length === 0) {
        setThreads([]);
        void writeCachedMailList(cacheKey, [], []);
        completed = true;
        return;
      }

      const params = new URLSearchParams({
        folder,
        account: viewAccount,
        // Tip 081: bound first paint — without limit the API walked Gmail until 100–150 threads.
        limit: String(MAIL_LIST_LIMIT),
      });
      // Soft/toolbar refresh must bypass the Hub API's 60s ATW Mail cache or new
      // mail stays invisible while mail.atw.net already shows it.
      if (soft || quiet) params.set("refresh", "1");
      const listPayload = await api<MailListResponse | MailThread[]>(`/api/mail?${params}`, {
        timeoutMs: MAIL_LIST_TIMEOUT_MS,
      });
      if (!stillCurrent()) return;
      const network = normalizeThreads(listPayload);
      const fromStore = await readCachedMailList(cacheKey);
      if (!stillCurrent()) return;
      // Keep richer chain/attach flags from IDB + in-memory detail hydrate.
      const keepHigherCount = folder !== "sent" && folder !== "drafts";
      let merged = mergeListMeta(network, fromStore, threadRowKey, keepHigherCount);
      // The API returns the newest page; older rows saved on this device stay listed.
      merged = mergeNewestWithCachedOlder(merged, fromStore, threadRowKey);
      // Single-account views must never keep foreign mailbox rows from a polluted cache.
      merged = filterThreadsForAccountView(merged, viewAccount);
      merged = merged.map((row) => {
        const detailRow = detailCacheRef.current.get(threadRowKey(row));
        if (!detailRow) return row;
        const email = threadAccountEmail(detailRow, nextAccounts);
        if (folder === "sent" || folder === "drafts") {
          const visible = messagesForMailFolder(detailRow.messages || [], folder, email);
          const listed = messagesForMailFolder(row.messages || [], folder, email);
          const folderCount = Math.max(visible.length, listed.length, 1);
          return {
            ...row,
            messageCount: folderCount,
            hasAttachments: Boolean(row.hasAttachments) || Boolean(detailRow.hasAttachments),
          };
        }
        return mergeListMeta([row], [detailRow], threadRowKey, true)[0]!;
      });
      setThreads(merged);
      hasPaintedListRef.current = merged.length > 0;
      void writeCachedMailList(cacheKey, merged, nextAccounts);
      completed = true;
      // Tip 123: list refresh must also pull the open chain — otherwise Gmail
      // replies land in the inbox row but the reader keeps a stale expanded cache.
      const openKey = selectedIdRef.current;
      if (openKey) {
        const openRow = merged.find((row) => threadRowKey(row) === openKey);
        const priorRow = fromStore?.find((row) => threadRowKey(row) === openKey);
        const openChanged =
          !priorRow ||
          priorRow.date !== openRow?.date ||
          priorRow.messageCount !== openRow?.messageCount;
        if (openRow && (!quiet || openChanged)) {
          detailFetchedAtRef.current.delete(openKey);
          void openThreadRef.current?.(openRow, { backgroundRefresh: true, force: true });
        }
      }
      if (!quiet && !windowSyncStartedRef.current) {
        windowSyncStartedRef.current = true;
        void syncMailWindow({
          accounts: nextAccounts,
          rowKeyOf: threadRowKey,
          apiIdOf: threadApiId,
          normalizeList: (payload) => normalizeThreads(payload as MailListResponse | MailThread[]),
          normalizeDetail: (row, payload) =>
            normalizeDetailPayload(row, payload as Parameters<typeof normalizeDetailPayload>[1]),
          onListUpdated: (syncedFolder, syncedAccount) =>
            windowSyncUpdatedRef.current?.(syncedFolder, syncedAccount),
          onListPageRateLimit: () => {
            if (mountedRef.current) setListPageRateBanner(GMAIL_LIST_RATE_LIMIT_BANNER);
          },
          onListPageRateLimitClear: () => {
            if (mountedRef.current) setListPageRateBanner("");
          },
          isCancelled: () => !mountedRef.current,
        });
      }
    } catch (err) {
      if (!stillCurrent()) return;
      const listMessage = mailErrorMessage(err, folder, viewAccount);
      if (/quota|rate limit/i.test(listMessage)) {
        quotaPauseRef.current = {
          until: Date.now() + 60_000,
          accountId: viewAccount === "all" ? "" : viewAccount,
        };
      }
      if (!quiet) setError(listMessage);
      // Keep cached rows on failure so reopen isn't an empty spinner.
      if (!soft && !paintedFromCache && !hasPaintedListRef.current) setThreads([]);
    } finally {
      if (quiet) {
        quietListInflightRef.current = false;
      } else if (stillCurrent()) {
        setLoading(false);
        setListRefreshing(false);
        setPullPx(0);
      }
      // Toast only for explicit soft refresh (pull / toolbar) — not every cold start.
      if (soft && !quiet && completed && stillCurrent()) {
        if (refreshNoticeTimer.current != null) {
          window.clearTimeout(refreshNoticeTimer.current);
          refreshNoticeTimer.current = null;
        }
        setRefreshNotice("Refresh completed");
        refreshNoticeTimer.current = window.setTimeout(() => {
          refreshNoticeTimer.current = null;
          setRefreshNotice("");
        }, 2000);
      }
    }
  }, [folder, accountFilter]);

  useEffect(() => {
    requestPersistentStorage();
    void loadList();
  }, [loadList]);

  useEffect(() => {
    loadListRef.current = loadList;
  }, [loadList]);

  // New-mail check while Hub is open: quiet poll + immediate check on return.
  useEffect(() => {
    const check = () => {
      if (document.visibilityState !== "visible") return;
      const pause = quotaPauseRef.current;
      if (pause && Date.now() < pause.until) {
        const viewing = accountFilterRef.current || "all";
        if (!pause.accountId || viewing === "all" || viewing === pause.accountId) return;
      }
      void loadListRef.current?.({ quiet: true });
    };
    const timer = window.setInterval(check, MAIL_NEW_MAIL_POLL_MS);
    document.addEventListener("visibilitychange", check);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
    };
  }, []);

  useEffect(() => {
    windowSyncUpdatedRef.current = (syncedFolder, syncedAccount) => {
      if (syncedFolder !== folder) return;
      const viewAccount = accountFilter || "all";
      if (viewAccount !== "all" && viewAccount !== syncedAccount) return;
      void readCachedMailList({ account: viewAccount, folder }).then((stored) => {
        if (!stored?.length) return;
        setThreads((current) =>
          filterThreadsForAccountView(
            mergeNewestWithCachedOlder(current, stored, threadRowKey),
            viewAccount,
          ),
        );
      });
    };
  }, [folder, accountFilter]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    writeMailNav({ account: accountFilter || "all", folder });
  }, [accountFilter, folder]);

  useEffect(() => {
    if (!urlSyncReadyRef.current) return;
    const apiId =
      selectedId && detail ? threadApiId(detail) || threadApiId({ id: selectedId, threadId: selectedId }) : "";
    const built = buildMailSearchParams({
      account: accountFilter || "all",
      folder,
      id: apiId || undefined,
    });
    setSearchParams(
      (prev) => {
        if (mailSearchParamsEqual(prev, built)) return prev;
        return built;
      },
      { replace: true },
    );
  }, [accountFilter, folder, selectedId, detail, setSearchParams]);

  useEffect(() => {
    const openId = pendingUrlOpenRef.current;
    if (!openId || urlOpenAttemptedRef.current) return;
    if (loading && !hasPaintedListRef.current) return;
    if (!accounts.length) return;

    const pool = [...threads];
    if (searchResult?.threads?.length) {
      const seen = new Set(pool.map(threadRowKey));
      for (const row of searchResult.threads) {
        if (!seen.has(threadRowKey(row))) pool.push(row);
      }
    }

    let match = findThreadForMailUrlId(openId, pool);
    if (!match) {
      match = stubThreadFromMailUrl(openId, {
        accountFilter: accountFilter || "all",
        accounts,
      });
    }

    urlOpenAttemptedRef.current = true;
    pendingUrlOpenRef.current = null;
    urlSyncReadyRef.current = true;
    void openThreadRef.current?.(match, { force: true });
  }, [loading, threads, searchResult, accounts, accountFilter]);

  useEffect(() => {
    return () => {
      if (refreshNoticeTimer.current != null) {
        window.clearTimeout(refreshNoticeTimer.current);
        refreshNoticeTimer.current = null;
      }
    };
  }, []);

  const PULL_REFRESH_THRESHOLD = 64;

  function onListPullStart(event: ReactTouchEvent<HTMLElement>) {
    if (!phoneLayout || listRefreshing || loading || selectedId) return;
    const pane = listPaneRef.current;
    if (!pane || pane.scrollTop > 2) return;
    pullStartY.current = event.touches[0]?.clientY ?? null;
    pullArmed.current = true;
  }

  function onListPullMove(event: ReactTouchEvent<HTMLElement>) {
    if (!pullArmed.current || pullStartY.current == null) return;
    const pane = listPaneRef.current;
    if (!pane || pane.scrollTop > 2) {
      pullStartY.current = null;
      pullArmed.current = false;
      setPullPx(0);
      return;
    }
    const dy = (event.touches[0]?.clientY ?? pullStartY.current) - pullStartY.current;
    if (dy <= 0) {
      // Scrolling up — abandon pull so the list can move freely.
      pullStartY.current = null;
      pullArmed.current = false;
      setPullPx(0);
      return;
    }
    // Resist so the gesture feels intentional; keep under ~120px.
    setPullPx(Math.min(120, dy * 0.42));
  }

  function onListPullEnd() {
    if (!pullArmed.current) return;
    const shouldRefresh = pullPx >= PULL_REFRESH_THRESHOLD && !listRefreshing && !loading;
    pullStartY.current = null;
    pullArmed.current = false;
    if (shouldRefresh) {
      setPullPx(PULL_REFRESH_THRESHOLD);
      void loadList({ soft: true });
      return;
    }
    setPullPx(0);
  }

  // Non-passive touchmove so iOS doesn't steal the pull for browser overscroll.
  // Only block when actively pulling down at the top — never while scrolling the list.
  useEffect(() => {
    const pane = listPaneRef.current;
    if (!pane || !phoneLayout) return;
    function onMove(event: TouchEvent) {
      if (!pullArmed.current || pullStartY.current == null) return;
      if (pane!.scrollTop > 2) {
        pullArmed.current = false;
        pullStartY.current = null;
        return;
      }
      const dy = (event.touches[0]?.clientY ?? pullStartY.current) - pullStartY.current;
      // Upward / no-pull motion must stay native (scroll back from bottom / mid-list).
      if (dy <= 0) {
        pullArmed.current = false;
        pullStartY.current = null;
        return;
      }
      event.preventDefault();
    }
    pane.addEventListener("touchmove", onMove, { passive: false });
    return () => pane.removeEventListener("touchmove", onMove);
  }, [phoneLayout]);

  useEffect(() => {
    function onLabels() {
      setLabelTick((tick) => tick + 1);
    }
    window.addEventListener(CONNECTION_DISPLAY_NAMES_EVENT, onLabels);
    window.addEventListener("storage", onLabels);
    return () => {
      window.removeEventListener(CONNECTION_DISPLAY_NAMES_EVENT, onLabels);
      window.removeEventListener("storage", onLabels);
    };
  }, []);

  useEffect(() => {
    function onNew() {
      const accountId = accounts[0]?.id || "";
      setComposeDraft(emptyComposeDraft(accountId));
    }
    window.addEventListener("atw-hub-new-message", onNew);
    return () => window.removeEventListener("atw-hub-new-message", onNew);
  }, [accounts]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest(".mail-sort-wrap")) return;
      setSortOpen(false);
      if (!t.closest(".mail-more-wrap")) setMoreMenuMessageId(null);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const openThread = useCallback(async (
    thread: MailThread,
    opts?: { backgroundRefresh?: boolean; force?: boolean },
  ) => {
    const rowKey = threadRowKey(thread);
    const apiId = threadApiId(thread);
    if (!apiId) {
      setError("This message has no id and cannot be opened.");
      return;
    }

    // Prefer a prefetched / cached hydrated detail so swipe settle never flashes
    // "Loading message…" after the peek already showed the full body.
    let cached = detailCacheRef.current.get(rowKey);
    // Commit often passes the already-hydrated peek object — seed the cache from it.
    // Only seed when fully expanded (multi-message or detailExpanded) so a list
    // stub with one body never blocks GET /api/mail/:id.
    if (!isMailThreadFullyExpanded(cached) && isMailThreadFullyExpanded(thread)) {
      cached = coerceMailThread(thread);
      detailCacheRef.current.set(rowKey, cached);
    }
    // Cold reopen: hydrate from IndexedDB before hitting the network.
    if (!isMailThreadFullyExpanded(cached)) {
      const fromDisk = await readCachedMailDetail(rowKey);
      if (fromDisk && isMailThreadHydrated(fromDisk)) {
        cached = coerceMailThread(fromDisk);
        detailCacheRef.current.set(rowKey, cached);
      }
    }
    let alreadyHydrated = isMailThreadHydrated(cached);
    let fullyExpanded = isMailThreadFullyExpanded(cached);
    const paint = alreadyHydrated && cached ? cached : coerceMailThread(thread);
    const waitingOnPrefetch = !fullyExpanded && prefetchInflight.current.has(rowKey);

    // Background re-pull of the already-open chain must not yank scroll,
    // accordion toggles, or attachment chrome mid-read.
    const sameOpenThread = selectedIdRef.current === rowKey;
    const quietRefresh = Boolean(opts?.backgroundRefresh) && sameOpenThread;

    setSelectedId(rowKey);
    setDetail(paint);
    // Prior long threads leave the pane scrolled; chrome (subject / Show details /
    // toolbar) would sit above the viewport while the white body card fills it.
    // Desktop scrolls `.mail-reader-pane`; phone scrolls `.mail-swipe-current`.
    if (!quietRefresh) {
      if (mailReaderPaneRef.current) mailReaderPaneRef.current.scrollTop = 0;
      const phoneScroll = swipeStageRef.current?.querySelector(
        ".mail-swipe-current",
      ) as HTMLElement | null;
      if (phoneScroll) phoneScroll.scrollTop = 0;
    }
    // Sync read state with the connected mailbox when opening an unread thread.
    if (thread.unread || paint.unread) {
      updateListedThreads((list) =>
        list.map((row) =>
          threadRowKey(row) === rowKey ? { ...row, unread: false } : row,
        ),
      );
      void api(mailActionPath(thread, "read"), { method: "POST" }).catch(() => {
        updateListedThreads((list) =>
          list.map((row) =>
            threadRowKey(row) === rowKey ? { ...row, unread: true } : row,
          ),
        );
      });
    }
    // If neighbor prefetch is still in flight, keep the reader interactive (no
    // "Loading message…") while we wait — same chrome the peek already showed.
    setDetailLoading(!alreadyHydrated && !waitingOnPrefetch);
    const order = readMailThreadOrder();
    const ordered = sortedMessages(paint, order);
    if (quietRefresh) {
      setCollapsedBodies((prev) => mergeCollapsedBodies(prev, ordered));
    } else {
      setExpandedAttachments({});
      setAttachmentOverflowExpanded({});
      // Stack the chain; collapse older bodies so the newest is open.
      setCollapsedBodies(defaultCollapsedBodies(ordered));
      setSwipeOffset(0);
      setSwipeDragging(false);
    }
    setError("");

    // Tip 123: only skip network when the chain is fully expanded AND the last
    // fetch is still fresh. Stale "fully expanded" cache hid Gmail-sent replies
    // (Kyle @ 3:59 still saw 3:26 as newest).
    const lastFetched = detailFetchedAtRef.current.get(rowKey) || 0;
    const stillFresh = Date.now() - lastFetched < DETAIL_FRESH_MS;
    if (fullyExpanded && stillFresh && !opts?.backgroundRefresh && !opts?.force) {
      return;
    }

    if (waitingOnPrefetch) {
      const deadline = Date.now() + 900;
      while (Date.now() < deadline && prefetchInflight.current.has(rowKey)) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 32));
        cached = detailCacheRef.current.get(rowKey);
        if (isMailThreadFullyExpanded(cached)) {
          fullyExpanded = true;
          alreadyHydrated = true;
          break;
        }
      }
      cached = detailCacheRef.current.get(rowKey);
      if (isMailThreadFullyExpanded(cached) && cached) {
        setDetail(cached);
        // Preserve accordion toggles while prefetch finishes painting.
        const nextOrdered = sortedMessages(cached, order);
        setCollapsedBodies((prev) => mergeCollapsedBodies(prev, nextOrdered));
        setDetailLoading(false);
        if (!opts?.backgroundRefresh) return;
      } else {
        setDetailLoading(true);
      }
    }

    try {
      const payload = await api<
        MailThread | { thread?: MailThread; messages?: MailMessage[]; message?: MailMessage }
      >(`/api/mail/${encodeURIComponent(apiId)}${mailDetailQuery(thread)}`);
      const normalized = mergePendingSent(
        mergeDetailWithListThread(thread, normalizeDetailPayload(thread, payload)),
      );
      if (selectedIdRef.current !== rowKey) return;
      detailFetchedAtRef.current.set(rowKey, Date.now());
      if (normalized.bodyPending) {
        const sec = Math.max(1, normalized.bodyError?.retryAfterSec || 60);
        quotaPauseRef.current = {
          until: Date.now() + sec * 1000,
          accountId: normalized.accountId || thread.accountId || "",
        };
      }
      setDetail(normalized);
      putDetailCache(normalized);
      // Detail refresh must not re-collapse the message the user is reading.
      const nextOrdered = sortedMessages(normalized, order);
      setCollapsedBodies((prev) => mergeCollapsedBodies(prev, nextOrdered));
    } catch (err) {
      // Keep whatever we painted (cached or optimistic) so the pane never blanks.
      const fallback = mergePendingSent(
        detailCacheRef.current.get(rowKey) || paint,
      );
      if (isMailThreadHydrated(fallback)) setDetail(fallback);
      else setDetail(paint);
      if (shouldCacheMailDetail(fallback)) putDetailCache(fallback);
      // Background refreshes (e.g. right after Send, before the provider has indexed
      // the new message) keep the painted thread instead of surfacing a 404.
      if (!opts?.backgroundRefresh) {
        const msg =
          err instanceof Error ? err.message : "Could not open message.";
        if (isAuthApiError(err)) {
          setError(msg);
        } else if (isMailThreadHydrated(fallback)) {
          // Cached thread is still readable — don't flash a scary banner on refresh hiccups.
          setError("");
          setRefreshNotice("Showing saved copy (couldn't refresh from server)");
          if (refreshNoticeTimer.current != null) {
            window.clearTimeout(refreshNoticeTimer.current);
          }
          refreshNoticeTimer.current = window.setTimeout(() => {
            refreshNoticeTimer.current = null;
            setRefreshNotice("");
          }, 4000);
        } else {
          setError(msg);
        }
      }
    } finally {
      setDetailLoading(false);
    }
  }, []);

  openThreadRef.current = openThread;
  selectedIdRef.current = selectedId;
  const threadsRef = useRef(threads);
  threadsRef.current = threads;

  // Tip 123: when Kyle returns to Hub (or stays on a thread), re-pull the chain
  // so provider-sent copies appear without a hard reopen.
  useEffect(() => {
    const refreshOpen = (fromFocus: boolean) => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      if (fromFocus) {
        const now = Date.now();
        if (now - lastFocusRefreshRef.current < 60_000) return;
        lastFocusRefreshRef.current = now;
      }
      const pause = quotaPauseRef.current;
      if (pause && Date.now() < pause.until) return;
      const key = selectedIdRef.current;
      if (!key) return;
      const row =
        threadsRef.current.find((thread) => threadRowKey(thread) === key) ||
        detailCacheRef.current.get(key);
      if (!row) return;
      const cached = detailCacheRef.current.get(key) || row;
      const bodies = cached.messages || [];
      if (
        !cached.bodyPending &&
        bodies.length > 0 &&
        bodies.every((message) => messageHasRealBody(message))
      ) {
        return;
      }
      detailFetchedAtRef.current.delete(key);
      void openThreadRef.current?.(row, { backgroundRefresh: true, force: true });
    };
    const onVis = () => {
      if (document.visibilityState === "visible") refreshOpen(true);
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    const timer = window.setInterval(() => refreshOpen(false), 120_000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
      window.clearInterval(timer);
    };
  }, []);

  const bodyPendingKey = detail?.bodyPending
    ? `${threadRowKey(detail)}:${detail.bodyError?.retryAfterSec || 60}`
    : "";
  useEffect(() => {
    if (!bodyPendingKey || !detail?.bodyPending) return;
    const sec = Math.max(1, detail.bodyError?.retryAfterSec || 60);
    const key = threadRowKey(detail);
    const tick = window.setInterval(() => setBodyRetryNow(Date.now()), 1000);
    let retry = 0;
    if (bodyAutoRetriedRef.current !== key) {
      retry = window.setTimeout(() => {
        bodyAutoRetriedRef.current = key;
        const row =
          threadsRef.current.find((thread) => threadRowKey(thread) === key) || detail;
        void openThreadRef.current?.(row, { backgroundRefresh: true, force: true });
      }, sec * 1000);
    }
    return () => {
      window.clearInterval(tick);
      if (retry) window.clearTimeout(retry);
    };
  }, [bodyPendingKey, detail]);

  const searchKey = searchTerm ? `${folder}\n${accountFilter || "all"}\n${searchTerm}` : "";
  const searching = Boolean(searchQuery) && (searchQuery !== searchTerm || searchResult?.key !== searchKey);

  useEffect(() => {
    if (!searchKey) {
      setSearchResult(null);
      setSearchError("");
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams({
      folder,
      account: accountFilter || "all",
      limit: String(MAIL_LIST_LIMIT),
      q: searchTerm,
    });
    api<(MailListResponse & { search?: string }) | MailThread[]>(`/api/mail?${params}`, {
      timeoutMs: MAIL_LIST_TIMEOUT_MS,
    })
      .then((payload) => {
        if (cancelled) return;
        setSearchResult({
          key: searchKey,
          threads: normalizeThreads(payload),
          serverMatched: !Array.isArray(payload) && payload.search === searchTerm,
        });
        setSearchError("");
      })
      .catch((err) => {
        if (cancelled) return;
        setSearchResult({ key: searchKey, threads: [], serverMatched: false });
        setSearchError(mailErrorMessage(err, folder, accountFilter || "all"));
      });
    return () => {
      cancelled = true;
    };
  }, [searchKey, searchTerm, folder, accountFilter]);

  const visibleThreads = useMemo(() => {
    let base = threads;
    if (searchQuery) {
      const local = threads.filter((thread) => threadMatchesSearch(thread, searchQuery));
      const remote =
        searchResult && searchResult.key === searchKey
          ? searchResult.threads.filter(
              (thread) =>
                (searchResult.serverMatched && searchQuery === searchTerm) ||
                threadMatchesSearch(thread, searchQuery),
            )
          : [];
      const seen = new Set(local.map(threadRowKey));
      base = [...local, ...remote.filter((thread) => !seen.has(threadRowKey(thread)))];
    }
    return sortThreads(filterThreads(base, filter), sortField, sortDir);
  }, [threads, filter, sortField, sortDir, searchQuery, searchTerm, searchResult, searchKey]);

  const selectedIndex = useMemo(() => {
    if (!selectedId) return -1;
    return visibleThreads.findIndex((thread) => threadRowKey(thread) === selectedId);
  }, [visibleThreads, selectedId]);

  // cacheTick keeps adjacent peeks in sync when prefetch lands.
  const prevListThread = selectedIndex > 0 ? visibleThreads[selectedIndex - 1] : undefined;
  const nextListThread =
    selectedIndex >= 0 && selectedIndex < visibleThreads.length - 1
      ? visibleThreads[selectedIndex + 1]
      : undefined;
  const prevPeek = resolveCachedThread(prevListThread);
  const nextPeek = resolveCachedThread(nextListThread);
  void cacheTick;

  const fetchThreadDetail = useCallback(async (thread: MailThread) => {
    const rowKey = threadRowKey(thread);
    const apiId = threadApiId(thread);
    if (!apiId || !rowKey) return;
    // Prefetch must expand the full chain — one-message stubs are not done.
    // Tip 123: also re-prefetch when the prior full fetch is stale.
    const lastFetched = detailFetchedAtRef.current.get(rowKey) || 0;
    const stillFresh = Date.now() - lastFetched < DETAIL_FRESH_MS;
    if (
      isMailThreadFullyExpanded(detailCacheRef.current.get(rowKey)) &&
      stillFresh
    ) {
      return;
    }
    if (prefetchInflight.current.has(rowKey)) return;
    prefetchInflight.current.add(rowKey);
    setPrefetchingKeys((prev) => ({ ...prev, [rowKey]: true }));
    try {
      const payload = await api<
        MailThread | { thread?: MailThread; messages?: MailMessage[]; message?: MailMessage }
      >(`/api/mail/${encodeURIComponent(apiId)}${mailDetailQuery(thread)}`);
      putDetailCache(
        mergePendingSent(
          mergeDetailWithListThread(thread, normalizeDetailPayload(thread, payload)),
        ),
      );
      detailFetchedAtRef.current.set(rowKey, Date.now());
    } catch {
      /* keep optimistic / list stub body under reader chrome */
    } finally {
      prefetchInflight.current.delete(rowKey);
      setPrefetchingKeys((prev) => {
        if (!prev[rowKey]) return prev;
        const next = { ...prev };
        delete next[rowKey];
        return next;
      });
    }
  }, []);

  // Phone: one neighbor, and only when this account is not in a quota cooldown.
  useEffect(() => {
    if (!phoneLayout || !selectedId || selectedIndex < 0) return;
    const pause = quotaPauseRef.current;
    if (pause && Date.now() < pause.until) return;
    const targets: MailThread[] = [];
    const neighbor =
      visibleThreads[selectedIndex + 1] || visibleThreads[selectedIndex - 1];
    if (neighbor) {
      const key = threadRowKey(neighbor);
      if (!isMailThreadFullyExpanded(detailCacheRef.current.get(key))) targets.push(neighbor);
    }
    let cursor = 0;
    const workers = Array.from({ length: Math.min(PREFETCH_CONCURRENCY, targets.length) }, async () => {
      while (cursor < targets.length) {
        const index = cursor;
        cursor += 1;
        await fetchThreadDetail(targets[index]);
      }
    });
    void Promise.all(workers);
  }, [phoneLayout, selectedId, selectedIndex, visibleThreads, fetchThreadDetail]);

  useEffect(() => {
    setSwipeOffset(0);
    setSwipeDragging(false);
    if (swipeCommitTimer.current) {
      window.clearTimeout(swipeCommitTimer.current);
      swipeCommitTimer.current = null;
    }
  }, [selectedId]);

  function resetSwipe(spring = false) {
    setSwipeDragging(false);
    setSwipeOffset(0);
    void spring;
  }

  function commitSwipe(deltaX: number) {
    const width = swipeWidth.current || window.innerWidth;
    const target = deltaX < 0 ? -width : width;
    const neighbor = deltaX < 0 ? nextListThread : prevListThread;
    // Kick the settle target immediately so body is ready when the slide lands.
    if (neighbor) void fetchThreadDetail(neighbor);
    setSwipeDragging(false);
    setSwipeOffset(target);
    if (swipeCommitTimer.current) window.clearTimeout(swipeCommitTimer.current);
    swipeCommitTimer.current = window.setTimeout(() => {
      if (neighbor) {
        const peek = deltaX < 0 ? nextPeek : prevPeek;
        const paint =
          peek && isMailThreadHydrated(peek)
            ? peek
            : resolveCachedThread(neighbor) || neighbor;
        void openThread(paint);
      }
      setSwipeOffset(0);
      swipeCommitTimer.current = null;
    }, SWIPE_ANIM_MS);
  }

  function swipeIgnoresTarget(target: EventTarget | null) {
    return target instanceof Element
      ? Boolean(target.closest("button, a, input, select, textarea, label, .mail-more-menu"))
      : false;
  }

  function readerScrollEl(): HTMLElement | null {
    return (
      (swipeStageRef.current?.querySelector(
        ".mail-swipe-current",
      ) as HTMLElement | null) || null
    );
  }

  function abandonReaderSwipe() {
    swipeStartX.current = null;
    swipeStartY.current = null;
    swipeAxis.current = null;
    setSwipeDragging(false);
  }

  function onSwipePointerDown(clientX: number, clientY: number, target: EventTarget | null) {
    if (!phoneLayout || !selectedId || !detail || swipeCommitTimer.current) return;
    if (swipeIgnoresTarget(target)) return;
    swipeStartX.current = clientX;
    swipeStartY.current = clientY;
    swipeAxis.current = null;
    swipeWidth.current = swipeStageRef.current?.clientWidth || window.innerWidth;
  }

  function onSwipePointerMove(clientX: number, clientY: number, event?: { preventDefault: () => void }) {
    if (swipeStartX.current == null || swipeStartY.current == null) return;
    const dx = clientX - swipeStartX.current;
    const dy = clientY - swipeStartY.current;
    if (swipeAxis.current == null) {
      if (Math.abs(dx) < 12 && Math.abs(dy) < 12) return;
      const scroller = readerScrollEl();
      if (scroller) {
        const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
        const atTop = scroller.scrollTop <= 1;
        const atBottom = scroller.scrollTop >= maxScroll - 1;
        // Finger moving up (dy < 0) while not at top → user is scrolling content up.
        // Finger moving down (dy > 0) while not at bottom → scrolling content down.
        // Never steal those for thread-swipe — that was the "stuck at bottom" bug.
        const scrollingUp = dy < 0 && !atTop;
        const scrollingDown = dy > 0 && !atBottom;
        if (scrollingUp || scrollingDown || Math.abs(dy) >= Math.abs(dx)) {
          abandonReaderSwipe();
          return;
        }
      } else if (Math.abs(dx) <= Math.abs(dy) * 1.5 || Math.abs(dx) < 24) {
        abandonReaderSwipe();
        return;
      }
      // Require a clear horizontal gesture before thread-swipe steals the pan.
      if (Math.abs(dx) <= Math.abs(dy) * 1.5 || Math.abs(dx) < 24) {
        abandonReaderSwipe();
        return;
      }
      swipeAxis.current = "x";
      // Axis locked horizontal — urgently hydrate the neighbor we're dragging toward.
      const toward = dx < 0 ? nextListThread : prevListThread;
      if (toward) void fetchThreadDetail(toward);
    }
    if (swipeAxis.current !== "x") return;
    event?.preventDefault();
    let next = dx;
    if ((dx > 0 && !prevPeek) || (dx < 0 && !nextPeek)) next = dx * 0.22;
    setSwipeDragging(true);
    setSwipeOffset(next);
  }

  function onSwipePointerUp(clientX: number) {
    if (swipeStartX.current == null) return;
    const startX = swipeStartX.current;
    const axis = swipeAxis.current;
    swipeStartX.current = null;
    swipeStartY.current = null;
    swipeAxis.current = null;
    if (axis !== "x") {
      resetSwipe();
      return;
    }
    const delta = clientX - startX;
    const threshold = Math.max(SWIPE_COMMIT_MIN_PX, swipeWidth.current * SWIPE_COMMIT_RATIO);
    if (delta <= -threshold && nextPeek) {
      commitSwipe(delta);
      return;
    }
    if (delta >= threshold && prevPeek) {
      commitSwipe(delta);
      return;
    }
    resetSwipe(true);
  }

  function onSwipeTouchStart(event: ReactTouchEvent<HTMLDivElement>) {
    const touch = event.changedTouches[0];
    if (!touch) return;
    onSwipePointerDown(touch.clientX, touch.clientY, event.target);
  }

  function onSwipeTouchMove(event: ReactTouchEvent<HTMLDivElement>) {
    const touch = event.touches[0];
    if (!touch) return;
    onSwipePointerMove(touch.clientX, touch.clientY, event);
  }

  function onSwipeTouchEnd(event: ReactTouchEvent<HTMLDivElement>) {
    const touch = event.changedTouches[0];
    onSwipePointerUp(touch?.clientX ?? 0);
  }

  const sortFieldLabel = SORT_FIELDS.find((s) => s.id === sortField)?.label || "Date";
  const sortLabel = `${sortFieldLabel} ${sortDirGlyph(sortDir)}`;
  const filterLabel = FILTER_OPTIONS.find((f) => f.id === filter)?.label || "All Messages";

  function peekTransitionStyle(transform: string): CSSProperties {
    return {
      transform,
      transition: swipeDragging ? "none" : `transform ${SWIPE_ANIM_MS}ms ease-out`,
    };
  }

  function toggleNav() {
    setNavCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(NAV_COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  /** Authenticated fetch of attachment bytes from the Hub API. */
  async function fetchAttachmentBlob(path: string): Promise<Blob | null> {
    const token = getToken();
    const headers = new Headers();
    if (token) headers.set("authorization", `Bearer ${token}`);
    const response = await fetch(`${apiBase()}${path}`, { headers });
    if (!response.ok) {
      // Surface API JSON errors instead of a silent null (Tip 193).
      try {
        const text = await response.text();
        const body = text ? (JSON.parse(text) as { message?: string }) : null;
        if (body?.message?.trim()) throw new Error(body.message.trim());
      } catch (err) {
        if (err instanceof Error && !(err instanceof SyntaxError)) throw err;
      }
      return null;
    }
    const contentType = (response.headers.get("content-type") || "").toLowerCase();
    if (contentType.includes("application/json")) return null;
    const blob = await response.blob();
    if (!blob.size) return null;
    return blob;
  }

  /** Resolve a browser-loadable attachment URL (inline cid: images + paperclip open). */
  async function resolveAttachmentUrl(
    file: MailAttachment,
    message: MailMessage | undefined,
    thread: MailThread | undefined,
  ): Promise<string | null> {
    const existing = attachmentOpenUrl(file);
    // Tip 132: only short-circuit when the href can load without our Bearer token.
    // Relative /api paths and api.hub.atw.net URLs need an authenticated fetch.
    if (existing && isDirectlyOpenableHref(existing)) {
      return absolutizeAttachmentHref(existing);
    }
    if (!file.id && !existing) return null;
    const threadForId = thread || detail || undefined;
    const threadId = threadForId ? threadApiId(threadForId) : "";
    if (!threadId && !existing) return null;
    const messageId = mailMessageIdForAttachment(message, threadForId, file);
    const cacheKey =
      file.id && threadId
        ? buildAttachmentCacheKey({
            accountId: thread?.accountId || detail?.accountId,
            threadId: String(threadId),
            messageId: String(messageId || "owner"),
            attachmentId: String(file.id),
          })
        : null;
    if (cacheKey) {
      const cachedUrl = await readCachedAttachmentObjectUrl(cacheKey);
      if (cachedUrl) return cachedUrl;
    }
    const paths: string[] = [];
    if (existing?.startsWith("/") && (!existing.includes("/attachments/") || existing.includes("messageId="))) {
      paths.push(existing);
    }
    if (file.id && threadId) {
      // Tip 210: try owner resolve first (API loads format=full). Stub messageIds 404'd
      // on /messages/:threadHex/attachments/... before Tip 210.
      paths.push(
        `/api/mail/${encodeURIComponent(threadId)}/attachments/${encodeURIComponent(file.id)}`,
      );
      if (messageId) {
        paths.push(
          `/api/mail/${encodeURIComponent(threadId)}/attachments/${encodeURIComponent(file.id)}?messageId=${encodeURIComponent(messageId)}`,
          `/api/mail/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(file.id)}`,
        );
      }
    }
    let lastErr: unknown;
    for (const path of paths) {
      try {
        // Attachment routes return raw bytes — skip api() JSON parse (mangles binary).
        const blob = await fetchAttachmentBlob(path);
        if (blob) {
          if (cacheKey) {
            void writeCachedAttachment(cacheKey, blob, {
              filename: file.filename,
              mimeType: file.mimeType,
            });
          }
          return URL.createObjectURL(blob);
        }
        // Some deployments still return JSON { url | dataUrl }.
        const payload = await api<{ url?: string; downloadUrl?: string; dataUrl?: string }>(path);
        const openable = pickOpenableAttachmentHref(payload);
        if (openable) return openable;
      } catch (err) {
        lastErr = err;
      }
    }
    if (lastErr) throw lastErr;
    return null;
  }

  function showAttachOpenError(message: string) {
    setError(message);
    // List-pane banners are hidden when the reader is full-width (phone / Mac app).
    setRefreshNotice(message);
    if (refreshNoticeTimer.current) window.clearTimeout(refreshNoticeTimer.current);
    refreshNoticeTimer.current = window.setTimeout(() => {
      setRefreshNotice("");
      refreshNoticeTimer.current = null;
    }, 6000);
  }

  async function openAttachment(
    file: MailAttachment,
    message: MailMessage,
    thread: MailThread,
    messageKey: string,
  ) {
    const files = Array.isArray(message.attachments) ? message.attachments : [];
    const fileIndex = Math.max(
      0,
      files.findIndex(
        (part) =>
          part === file ||
          (file.id && part.id && part.id === file.id) ||
          (!file.id &&
            !part.id &&
            (file.filename || "") === (part.filename || "")),
      ),
    );
    const label = attachmentLabel(file, fileIndex >= 0 ? fileIndex : 0);
    const rowKey = attachmentRowKey(file, label, message.id, messageKey);
    setOpeningAttachmentKey(rowKey);
    setError("");
    try {
      // Tip 132: use the message that owns the file (was hard-coded to messages[0]).
      const href = await resolveAttachmentUrl(file, message, thread);
      if (!href) {
        showAttachOpenError(
          file.id ? "Attachment URL missing from API." : "This attachment cannot be opened yet.",
        );
        return;
      }
      // iPhone document viewers replace this screen and hide Close / Share / Open with.
      if (isHubIosShell() && !isImageAttachment(file)) {
        if (canNativePreviewFile()) {
          try {
            const opened = await openWithAttachmentHref(href, label, file.mimeType);
            if (opened === "preview") return;
          } catch (err) {
            showAttachOpenError(err instanceof Error ? err.message : "Could not open this file.");
          }
        }
        setAttachPreview({
          items: [
            {
              href,
              label,
              mimeType: (file.mimeType || "").trim(),
            },
          ],
          index: 0,
        });
        return;
      }
      if (canPreviewAttachment(file, href)) {
        const gen = ++attachPreviewGenRef.current;
        const mimeType =
          (file.mimeType || "").trim() ||
          (isImageAttachment(file)
            ? `image/${(label.split(".").pop() || "png").toLowerCase().replace("jpg", "jpeg")}`
            : isPdfAttachment(file)
              ? "application/pdf"
              : "");
        const clicked = {
          href,
          label,
          mimeType,
        };
        // Tip 209: open the clicked file immediately. Waiting on every sibling
        // made clicks feel dead on messages with many images (felt like no-op).
        setAttachPreview({ items: [clicked], index: 0 });
        setOpeningAttachmentKey(null);

        if (!isImageAttachment(file)) return;

        const siblings = files.filter(isImageAttachment);
        if (siblings.length <= 1) return;

        void (async () => {
          const items: { href: string; label: string; mimeType: string }[] = [];
          let start = 0;
          for (let i = 0; i < siblings.length; i++) {
            if (attachPreviewGenRef.current !== gen) return;
            const sibling = siblings[i];
            const siblingLabel = attachmentLabel(sibling, i);
            const same =
              (file.id && sibling.id && file.id === sibling.id) ||
              sibling === file ||
              (!file.id &&
                !sibling.id &&
                (file.filename || "") === (sibling.filename || ""));
            if (same) {
              start = items.length;
              items.push({
                href,
                label: siblingLabel || label,
                mimeType: sibling.mimeType || file.mimeType || "",
              });
              continue;
            }
            try {
              const siblingHref = await resolveAttachmentUrl(sibling, message, thread);
              if (!siblingHref || !canPreviewAttachment(sibling, siblingHref)) continue;
              items.push({
                href: siblingHref,
                label: siblingLabel,
                mimeType: sibling.mimeType || "",
              });
            } catch {
              /* skip unresolved sibling */
            }
          }
          if (attachPreviewGenRef.current !== gen || items.length <= 1) return;
          setAttachPreview((prev) => {
            if (!prev || attachPreviewGenRef.current !== gen) return prev;
            const currentLabel = prev.items[prev.index]?.label;
            const nextIndex = Math.max(
              0,
              items.findIndex((item) => item.label === currentLabel || item.href === href),
            );
            return { items, index: nextIndex >= 0 ? nextIndex : start };
          });
        })();
        return;
      }
      if (href.startsWith("data:") || href.startsWith("blob:")) {
        await downloadAttachmentHref(href, label);
        return;
      }
      if (isHubNativeShell()) {
        await downloadAttachmentHref(href, label);
        return;
      }
      const opened = window.open(href, "_blank", "noopener,noreferrer");
      if (!opened) await downloadAttachmentHref(href, label);
    } catch (err) {
      showAttachOpenError(
        err instanceof Error ? err.message : "Could not open attachment.",
      );
    } finally {
      setOpeningAttachmentKey(null);
    }
  }

  async function fetchAttachmentUrlForInline(
    file: MailAttachment,
    message: MailMessage,
    thread: MailThread,
  ): Promise<string | null> {
    try {
      return await resolveAttachmentUrl(file, message, thread);
    } catch {
      return null;
    }
  }

  async function fetchCalendarInviteText(
    file: MailAttachment,
    message: MailMessage,
    thread: MailThread,
  ): Promise<string | null> {
    try {
      const href = await resolveAttachmentUrl(file, message, thread);
      if (!href) return null;
      const response = await fetch(href);
      if (!response.ok) return null;
      const text = await response.text();
      return text && /BEGIN:VCALENDAR/i.test(text) ? text : null;
    } catch {
      return null;
    }
  }

  function accountForThread(thread: MailThread): MailAccount | undefined {
    return accounts.find((account) => account.id === thread.accountId) || accounts[0];
  }

  function openComposeForMessage(mode: ComposeMode, thread: MailThread, message: MailMessage) {
    setMoreMenuMessageId(null);
    setComposeDraft(
      buildComposeDraft({
        mode,
        thread,
        message,
        account: accountForThread(thread),
      }),
    );
  }

  function openEditDraft(thread: MailThread, message: MailMessage) {
    setMoreMenuMessageId(null);
    const draft = draftFromExistingMessage({
      thread,
      message,
      account: accountForThread(thread),
    });
    setComposeDraft(draft);
    // Tip 215: saved drafts keep attachment metadata on the message, but compose
    // needs bytes. Hydrate after open so Edit draft does not look attachment-free.
    const files = Array.isArray(message.attachments) ? message.attachments : [];
    if (!files.length || !message.id) return;
    const sourceMessageId = message.id;
    void (async () => {
      try {
        const attachments = await hydrateDraftAttachments(files, (file) =>
          resolveAttachmentUrl(file, message, thread),
        );
        setComposeDraft((prev) => {
          if (!prev || prev.sourceMessageId !== sourceMessageId) return prev;
          if (!attachments.length) return prev;
          return { ...prev, attachments };
        });
        if (!attachments.length) {
          setError("Could not load this draft's attachments. Re-attach the files before sending.");
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Could not load this draft's attachments. Re-attach the files before sending.",
        );
      }
    })();
  }

  async function starThread(thread: MailThread) {
    const id = threadApiId(thread);
    if (!id) return;
    try {
      const updated = await api<MailThread>(mailActionPath(thread, "star"), {
        method: "POST",
      });
      const next = coerceMailThread({ ...thread, ...updated, starred: updated.starred ?? !thread.starred });
      updateListedThreads((list) =>
        list.map((row) => (threadRowKey(row) === threadRowKey(thread) ? { ...row, starred: next.starred } : row)),
      );
      if (detail && threadRowKey(detail) === threadRowKey(thread)) {
        setDetail({ ...detail, starred: next.starred });
        putDetailCache({ ...detail, starred: next.starred });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update star.");
    }
  }

  async function markRead(thread: MailThread) {
    const id = threadApiId(thread);
    if (!id) return;
    try {
      await api(mailActionPath(thread, "read"), { method: "POST" });
      updateListedThreads((list) =>
        list.map((row) => (threadRowKey(row) === threadRowKey(thread) ? { ...row, unread: false } : row)),
      );
      if (detail && threadRowKey(detail) === threadRowKey(thread)) {
        setDetail({ ...detail, unread: false });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not mark read.");
    }
  }

  async function markUnread(thread: MailThread) {
    const id = threadApiId(thread);
    if (!id) return;
    try {
      await api(mailActionPath(thread, "unread"), { method: "POST" });
      updateListedThreads((list) =>
        list.map((row) => (threadRowKey(row) === threadRowKey(thread) ? { ...row, unread: true } : row)),
      );
      if (detail && threadRowKey(detail) === threadRowKey(thread)) {
        setDetail({ ...detail, unread: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not mark unread.");
    }
  }

  async function archiveThread(thread: MailThread) {
    const id = threadApiId(thread);
    if (!id) return;
    try {
      await api(mailActionPath(thread, "archive"), { method: "POST" });
      updateListedThreads((list) => list.filter((row) => threadRowKey(row) !== threadRowKey(thread)));
      if (selectedId && selectedId === threadRowKey(thread)) {
        setSelectedId(null);
        setDetail(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive.");
    }
  }

  async function notSpamThread(thread: MailThread) {
    const id = threadApiId(thread);
    if (!id) return;
    try {
      await api(mailActionPath(thread, "not-spam"), { method: "POST" });
      updateListedThreads((list) => list.filter((row) => threadRowKey(row) !== threadRowKey(thread)));
      if (selectedId && selectedId === threadRowKey(thread)) {
        setSelectedId(null);
        setDetail(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not mark as not spam.");
    }
  }

  async function deleteThread(thread: MailThread, opts?: { confirm?: boolean }) {
    const id = threadApiId(thread);
    if (!id) return;
    if (opts?.confirm !== false && !window.confirm("Delete this conversation?")) return;
    try {
      await api(mailActionPath(thread, "delete"), { method: "POST" });
      updateListedThreads((list) => list.filter((row) => threadRowKey(row) !== threadRowKey(thread)));
      setSelectedId(null);
      setDetail(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete.");
    }
  }

  function applyListSwipeAction(thread: MailThread, action: MailSwipeActionId) {
    if (action === "none") return;
    if (action === "archive") {
      void archiveThread(thread);
      return;
    }
    if (action === "delete") {
      void deleteThread(thread, { confirm: true });
      return;
    }
    if (action === "read") {
      void markRead(thread);
      return;
    }
    if (action === "unread") {
      void markUnread(thread);
      return;
    }
    if (action === "toggle-read") {
      if (thread.unread) void markRead(thread);
      else void markUnread(thread);
      return;
    }
    if (action === "star") {
      void starThread(thread);
    }
  }

  async function dismissDraft(thread: MailThread, message: MailMessage) {
    const threadId = threadApiId(thread);
    if (!threadId || !message.id) return;
    if (!window.confirm("Discard this draft?")) return;
    try {
      const result = await api<{ removedThread?: boolean; thread?: MailThread }>(
        `/api/mail/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(message.id)}/dismiss-draft`,
        { method: "POST" },
      );
      if (result.removedThread) {
        updateListedThreads((list) => list.filter((row) => threadRowKey(row) !== threadRowKey(thread)));
        setSelectedId(null);
        setDetail(null);
        return;
      }
      if (result.thread) {
        const next = coerceMailThread(result.thread);
        putDetailCache(next);
        setDetail(next);
        updateListedThreads((list) =>
          list.map((row) => (threadRowKey(row) === threadRowKey(thread) ? { ...row, ...next } : row)),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not discard draft.");
    }
  }

  const readerProps = {
    accounts,
    folder,
    threadOrder,
    clockFormat,
    expandedAttachments,
    attachmentOverflowExpanded,
    collapsedBodies,
    moreMenuMessageId,
    onToggleAttachments: (messageKey: string) =>
      setExpandedAttachments((prev) => ({
        ...prev,
        // Tip 207: true = detail rows; unset/false = compact preview (never fully hidden).
        [messageKey]: !prev[messageKey],
      })),
    onToggleAttachmentOverflow: (messageKey: string) =>
      setAttachmentOverflowExpanded((prev) => ({
        ...prev,
        [messageKey]: !prev[messageKey],
      })),
    onToggleBody: (messageKey: string) =>
      setCollapsedBodies((prev) => ({ ...prev, [messageKey]: !prev[messageKey] })),
    onOpenAttachment: (
      file: MailAttachment,
      message: MailMessage,
      thread: MailThread,
      messageKey: string,
    ) => {
      void openAttachment(file, message, thread, messageKey);
    },
    openingAttachmentKey,
    onFetchAttachmentUrl: fetchAttachmentUrlForInline,
    onFetchCalendarInviteText: fetchCalendarInviteText,
    onStarThread: (thread: MailThread) => {
      void starThread(thread);
    },
    onCompose: openComposeForMessage,
    onEditDraft: openEditDraft,
    onDismissDraft: (thread: MailThread, message: MailMessage) => {
      void dismissDraft(thread, message);
    },
    onToggleMoreMenu: setMoreMenuMessageId,
    onMarkUnread: (thread: MailThread) => {
      void markUnread(thread);
    },
    onArchive: (thread: MailThread) => {
      void archiveThread(thread);
    },
    onNotSpam: (thread: MailThread) => {
      void notSpamThread(thread);
    },
    onDelete: (thread: MailThread) => {
      void deleteThread(thread);
    },
    bodyRetrySec: detail?.bodyPending
      ? Math.max(0, Math.ceil(((quotaPauseRef.current?.until || 0) - bodyRetryNow) / 1000))
      : 0,
    onRetryBody: () => {
      if (!detail) return;
      bodyAutoRetriedRef.current = threadRowKey(detail);
      void openThread(detail, { force: true, backgroundRefresh: true });
    },
  };

  function renderSwipeReader(thread: MailThread, active: boolean): ReactNode {
    const key = threadRowKey(thread);
    const loading = active
      ? detailLoading && !isMailThreadHydrated(thread)
      : Boolean(prefetchingKeys[key]) && !isMailThreadHydrated(thread);
    return (
      <MailThreadReader
        thread={thread}
        active={active}
        loading={loading}
        {...readerProps}
        onBack={active ? closeReader : undefined}
      />
    );
  }

  function closeReader() {
    setSelectedId(null);
    setDetail(null);
  }

  function renderFolderTreeNav(): ReactNode {
    return (
      <div className="mail-tree" aria-label="Folder tree">
        <div className="mail-tree-branch">
          <button
            type="button"
            className={accountFilter === "all" ? "mail-tree-account active" : "mail-tree-account"}
            aria-expanded={Boolean(treeExpanded.all)}
            onClick={() => toggleTreeBranch("all")}
          >
            <span className="mail-tree-caret" aria-hidden>
              {treeExpanded.all ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
            <span className="mail-tree-stack">
              <strong>All Inboxes</strong>
              <small>
                {accounts.length} account{accounts.length === 1 ? "" : "s"}
              </small>
            </span>
          </button>
          {treeExpanded.all ? (
            <div className="mail-tree-folders">
              {MAIL_FOLDERS.map((item) => (
                <button
                  key={`all-${item.id}`}
                  type="button"
                  className={
                    accountFilter === "all" && folder === item.id
                      ? "mail-tree-folder active"
                      : "mail-tree-folder"
                  }
                  onClick={() => selectAccountFolder("all", item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {accounts.map((account) => {
          const open = Boolean(treeExpanded[account.id]);
          return (
            <div key={account.id} className="mail-tree-branch">
              <button
                type="button"
                className={
                  accountFilter === account.id ? "mail-tree-account active" : "mail-tree-account"
                }
                title={account.email}
                aria-expanded={open}
                onClick={() => toggleTreeBranch(account.id)}
              >
                <span className="mail-tree-caret" aria-hidden>
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
                <span className="mail-tree-stack">
                  <strong>{providerLabel(account)}</strong>
                  <small className="mail-email-truncate">{accountOptionLabel(account)}</small>
                </span>
              </button>
              {open ? (
                <div className="mail-tree-folders">
                  {foldersForAccountNav(account.id, providerFolders).map((item) => (
                    <button
                      key={`${account.id}-${item.id}`}
                      type="button"
                      className={
                        accountFilter === account.id && folder === item.id
                          ? "mail-tree-folder active"
                          : "mail-tree-folder"
                      }
                      onClick={() => selectAccountFolder(account.id, item.id)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  // Re-read local display names when Connections edits them.
  void labelTick;

  if (!loading && accounts.length === 0) {
    return (
      <div className="page-pad mail-empty-accounts">
        <h1>You haven&apos;t connected an account</h1>
        <p className="muted">
          Connect Google Mail in Settings → Connections to see your inbox here.
        </p>
        {error ? (
          <p className="banner-error" role="alert">
            {error}
          </p>
        ) : null}
        <Link className="primary-btn settings-link-btn" to="/settings/connections">
          Go to Connections
        </Link>
      </div>
    );
  }

  return (
    <div className={`mail-layout${navCollapsed ? " mail-nav-collapsed" : ""}`}>
      <aside
        className={`mail-nav-pane desktop-only${navCollapsed ? " mail-nav-pane-collapsed" : ""}`}
        aria-label="Mail folders"
      >
        <div className="mail-nav-compose-row">
          {navCollapsed ? (
            <>
              <button
                type="button"
                className="mail-compose-btn mail-compose-icon"
                onClick={() => setComposeDraft(emptyComposeDraft(accounts[0]?.id || ""))}
                title="New message"
                aria-label="New message"
              >
                <Plus size={18} />
              </button>
              <button
                type="button"
                className="mail-nav-collapse-btn"
                aria-label="Expand folders"
                title="Expand"
                onClick={toggleNav}
              >
                <ChevronRight size={16} />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="mail-compose-btn"
                onClick={() => setComposeDraft(emptyComposeDraft(accounts[0]?.id || ""))}
              >
                <Plus size={16} />
                New Message
              </button>
              <button
                type="button"
                className="mail-nav-collapse-btn"
                aria-label="Collapse folders"
                title="Collapse"
                onClick={toggleNav}
              >
                <ChevronLeft size={16} />
              </button>
            </>
          )}
        </div>

        {!navCollapsed ? (
          mailView === "tree" ? (
            <>
              <p className="mail-nav-kicker">Emails</p>
              {renderFolderTreeNav()}
            </>
          ) : mailView === "switcher" ? (
            <>
              <p className="mail-nav-kicker">Account</p>
              <div className="mail-account-filter mail-account-switcher">
                <select
                  aria-label="Mail account"
                  value={accountFilter}
                  onChange={(e) => {
                    const next = e.target.value;
                    setAccountFilter(next);
                    setSelectedId(null);
                    setDetail(null);
                  }}
                >
                  {accounts.length > 1 ? <option value="all">All Inboxes</option> : null}
                  {accounts.length === 1 ? (
                    <option value="all">{accountOptionLabel(accounts[0]!)}</option>
                  ) : (
                    accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {accountOptionLabel(a)}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <p className="mail-nav-kicker">Emails</p>
              <div className="mail-tree-folders mail-folder-list" role="tablist" aria-label="Mail folders">
                {foldersForAccountNav(
                  accountFilter === "all" && accounts.length === 1
                    ? accounts[0]!.id
                    : accountFilter,
                  providerFolders,
                ).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={folder === item.id}
                    className={
                      folder === item.id ? "mail-tree-folder active" : "mail-tree-folder"
                    }
                    onClick={() => {
                      setFolder(item.id);
                      setSelectedId(null);
                      setDetail(null);
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <p className="mail-nav-kicker">Account</p>
              <div className="mail-account-chips" role="listbox" aria-label="Mail accounts">
                {accounts.length > 1 ? (
                  <button
                    type="button"
                    role="option"
                    aria-selected={accountFilter === "all"}
                    className={accountFilter === "all" ? "mail-chip active" : "mail-chip"}
                    onClick={() => {
                      setAccountFilter("all");
                      setSelectedId(null);
                      setDetail(null);
                    }}
                  >
                    All Inboxes
                  </button>
                ) : null}
                {accounts.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    role="option"
                    aria-selected={accountFilter === a.id || accounts.length === 1}
                    className={
                      accountFilter === a.id || accounts.length === 1
                        ? "mail-chip active"
                        : "mail-chip"
                    }
                    title={a.email}
                    onClick={() => {
                      setAccountFilter(a.id);
                      setSelectedId(null);
                      setDetail(null);
                    }}
                  >
                    {truncateEmail(accountOptionLabel(a))}
                    <ChevronDown size={14} className="mail-chip-chevron" aria-hidden />
                  </button>
                ))}
              </div>

              <p className="mail-nav-kicker">Emails</p>
              <div className="mail-folder-chips" role="tablist" aria-label="Mail folders">
                {foldersForAccountNav(
                  accountFilter === "all" && accounts.length === 1
                    ? accounts[0]!.id
                    : accountFilter,
                  providerFolders,
                ).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={folder === item.id}
                    className={folder === item.id ? "mail-chip folder active" : "mail-chip folder"}
                    onClick={() => {
                      setFolder(item.id);
                      setSelectedId(null);
                      setDetail(null);
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </>
          )
        ) : null}
      </aside>
      {!navCollapsed ? (
        <PaneResizer id="mail.nav" cssVar="--mail-nav-w" min={180} max={380} label="Resize folders" />
      ) : null}

      <section
        className={`mail-list-pane${pullPx > 0 || listRefreshing ? " is-pulling" : ""}`}
        ref={listPaneRef}
        onTouchStart={onListPullStart}
        onTouchMove={onListPullMove}
        onTouchEnd={onListPullEnd}
        onTouchCancel={onListPullEnd}
      >
        {phoneLayout ? (
          <div
            className={`mail-pull-refresh${listRefreshing ? " is-refreshing" : ""}${
              pullPx >= PULL_REFRESH_THRESHOLD ? " is-ready" : ""
            }`}
            style={{ height: listRefreshing ? 48 : pullPx }}
            aria-hidden={pullPx < 8 && !listRefreshing}
          >
            <RefreshCw
              size={18}
              className={listRefreshing ? "mail-pull-spinner spinning" : "mail-pull-spinner"}
              style={
                listRefreshing
                  ? undefined
                  : { transform: `rotate(${Math.min(180, (pullPx / PULL_REFRESH_THRESHOLD) * 180)}deg)` }
              }
              aria-hidden
            />
            <span>
              {listRefreshing
                ? "Refreshing…"
                : pullPx >= PULL_REFRESH_THRESHOLD
                  ? "Release to refresh"
                  : "Pull to refresh"}
            </span>
          </div>
        ) : null}
        <div className="mail-list-toolbar">
          <div className="mail-sort-wrap">
            <button
              type="button"
              className="mail-sort-trigger"
              aria-expanded={sortOpen}
              onClick={() => setSortOpen((v) => !v)}
            >
              <strong>{sortLabel}</strong>
              <span className="muted"> · {filterLabel}</span>
              <ChevronDown size={14} />
            </button>
            {sortOpen ? (
              <div className="mail-sort-sheet" role="dialog" aria-label="Sort and filter">
                <p className="mail-nav-kicker">Sort</p>
                {SORT_FIELDS.map((opt) => {
                  const active = sortField === opt.id;
                  const DirIcon = sortDir === "desc" ? ArrowDown : ArrowUp;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={active ? "mail-sort-option active" : "mail-sort-option"}
                      aria-pressed={active}
                      aria-label={
                        active
                          ? `${opt.label}, ${sortDirAria(opt.id, sortDir)}. Activate to reverse.`
                          : `Sort by ${opt.label}`
                      }
                      onClick={() => selectSortField(opt.id)}
                    >
                      <span className="mail-sort-option-label">{opt.label}</span>
                      <span className="mail-sort-option-meta" aria-hidden>
                        {active ? (
                          <>
                            <DirIcon size={14} strokeWidth={2.5} className="mail-sort-dir-icon" />
                            <Check size={14} strokeWidth={2.5} className="mail-sort-check" />
                          </>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
                <p className="mail-nav-kicker">Filter</p>
                <div className="mail-filter-row">
                  {FILTER_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={filter === opt.id ? "mail-chip active" : "mail-chip"}
                      onClick={() => {
                        setFilter(opt.id);
                        setSortOpen(false);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="mail-nav-kicker">List grouping</p>
                <div className="mail-filter-row">
                  <button
                    type="button"
                    className={conversations ? "mail-chip active" : "mail-chip"}
                    onClick={() => {
                      setConversations(true);
                      writeMailConversations(true);
                      setSortOpen(false);
                    }}
                  >
                    Email chain
                  </button>
                  <button
                    type="button"
                    className={!conversations ? "mail-chip active" : "mail-chip"}
                    onClick={() => {
                      setConversations(false);
                      writeMailConversations(false);
                      setSortOpen(false);
                    }}
                  >
                    Separate rows
                  </button>
                </div>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="icon-btn"
            aria-label="Refresh"
            disabled={loading || listRefreshing}
            onClick={() => void loadList({ soft: Boolean(threads.length) })}
          >
            <RefreshCw
              size={18}
              className={loading || listRefreshing ? "spinning" : undefined}
            />
          </button>
        </div>

        {error ? <p className="banner-error">{error}</p> : null}
        {listPageRateBanner ? (
          <p className="banner-error mail-list-rate-banner" role="status">
            {listPageRateBanner}
          </p>
        ) : null}
        {refreshNotice ? (
          <div className="mail-refresh-toast" role="status" aria-live="polite">
            <span>{refreshNotice}</span>
            <button
              type="button"
              className="mail-refresh-toast-dismiss"
              aria-label="Dismiss"
              onClick={dismissRefreshNotice}
            >
              <X size={14} strokeWidth={2.5} aria-hidden />
            </button>
          </div>
        ) : null}
        {loading ? (
          <div className="mail-list-skeleton" aria-busy="true" aria-label="Loading mail">
            <p className="muted pad">Looking for mail…</p>
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="mail-skel-row" />
            ))}
          </div>
        ) : null}
        {searchQuery && searchError ? <p className="banner-error">{searchError}</p> : null}
        {searchQuery && searching && visibleThreads.length ? (
          <p className="muted pad" role="status">
            Searching {folderLabel(folder).toLowerCase()}…
          </p>
        ) : null}
        {!loading && !visibleThreads.length && !error ? (
          <p className="muted pad" role="status">
            {!searchQuery
              ? "No messages in this folder."
              : searching
                ? `Searching ${folderLabel(folder).toLowerCase()}…`
                : `No messages in ${folderLabel(folder).toLowerCase()} match “${searchQuery}”.`}
          </p>
        ) : null}

        <ul className="mail-thread-list">
          {visibleThreads.map((thread) => {
            const rowKey = threadRowKey(thread);
            const acct = accountLabel(accounts, thread.accountId);
            const showAccount = accounts.length > 1 && accountFilter === "all" && Boolean(acct);
            // Inbox counts the whole chain. Sent counts only the mail we sent.
            const rowEmail = threadAccountEmail(thread, accounts);
            const chainCount = listChainCount(thread, folder, rowEmail);
            const newest = [...(thread.messages || [])].reduce<MailMessage | null>((best, message) => {
              if (!best) return message;
              const bestAt = best.date ? Date.parse(best.date) : 0;
              const at = message.date ? Date.parse(message.date) : 0;
              return at >= bestAt ? message : best;
            }, null);
            const showSent = folder === "sent" || Boolean(newest && messageIsSent(newest, rowEmail));
            const hasAttach = threadHasAttachments(thread);
            const snippet = safeText(thread.snippet);
            return (
              <MailListRow
                key={rowKey}
                thread={thread}
                selected={selectedId === rowKey}
                showAccount={showAccount}
                accountLabel={showAccount ? truncateEmail(acct, 22) : undefined}
                fromLabel={displayFrom(thread, acct || undefined)}
                chainCount={chainCount}
                showSent={showSent}
                hasAttach={hasAttach}
                listWhen={formatListWhen(thread.date, clockFormat)}
                subject={safeText(thread.subject) || "(no subject)"}
                snippet={snippet}
                leftAction={swipeLeft}
                rightAction={swipeRight}
                onOpen={() => void openThread(thread)}
                onSwipeAction={(action) => applyListSwipeAction(thread, action)}
              />
            );
          })}
        </ul>
      </section>
      <PaneResizer id="mail.list" cssVar="--mail-list-w" min={260} max={720} label="Resize message list" />

      <section
        ref={mailReaderPaneRef}
        className={`mail-reader-pane${selectedId || composeDraft ? " open" : ""}${composeDraft ? " composing" : ""}`}
        data-pane-main
        data-mail-reader={composeDraft ? "compose" : selectedId ? "open" : "empty"}
        aria-live="polite"
      >
        {composeDraft ? (
          <MailComposePanel
            accounts={accounts}
            draft={composeDraft}
            onChange={setComposeDraft}
            onClose={() => setComposeDraft(null)}
            onSent={(sent: SentMailInfo) => {
              setComposeDraft(null);
              // Tip 122: always paint the sent message into the chain. Tip 121
              // only merged when threadId matched and then refetch could skip or
              // wipe it — Kyle never saw what he just sent.
              const fromAccount = accounts.find((account) => account.id === sent.accountId);
              const fromEmail = (fromAccount?.email || "").trim();
              const fromLabel = fromAccount
                ? accountOptionLabel(fromAccount)
                : fromEmail || "Me";
              const localId = sent.serverMessageId || `local-sent-${Date.now()}`;
              const openKey =
                (detail ? threadRowKey(detail) : "") ||
                sent.sourceThreadKey ||
                selectedId ||
                "";
              const sameOpenChain =
                Boolean(detail) &&
                (Boolean(sent.sourceThreadKey && sent.sourceThreadKey === threadRowKey(detail!)) ||
                  Boolean(
                    sent.threadId &&
                      (sent.threadId === detail!.id || sent.threadId === detail!.threadId),
                  ) ||
                  (sent.mode !== "new" && Boolean(selectedId) && threadRowKey(detail!) === selectedId));

              const targetId =
                sent.serverThreadId ||
                sent.threadId ||
                (sameOpenChain ? detail!.threadId || detail!.id : "") ||
                localId;
              const baseThread: MailThread = sameOpenChain && detail
                ? detail
                : {
                    id: targetId,
                    threadId: targetId,
                    accountId: sent.accountId,
                    subject: sent.subject,
                    from: fromLabel,
                    fromName: fromLabel,
                    fromEmail: fromEmail || undefined,
                    snippet: sent.bodyText.slice(0, 140),
                    date: new Date().toISOString(),
                    messages: [],
                    messageCount: 0,
                  };

              const optimistic = coerceMailMessage(
                {
                  id: localId,
                  threadId: baseThread.threadId || baseThread.id,
                  accountId: sent.accountId,
                  subject: sent.subject,
                  from: fromLabel,
                  fromName: fromLabel,
                  fromEmail: fromEmail || undefined,
                  to: sent.to,
                  cc: sent.cc || undefined,
                  date: new Date().toISOString(),
                  // Prefer HTML so short plain bodies aren't treated as list stubs.
                  bodyText: sent.bodyText,
                  bodyHtml:
                    sent.bodyHtml ||
                    (sent.bodyText ? `<p>${escapeHtml(sent.bodyText).replace(/\n/g, "<br>")}</p>` : undefined),
                  snippet: sent.bodyText.slice(0, 140) || "(sent)",
                },
                localId,
              );

              const prior = Array.isArray(baseThread.messages) ? baseThread.messages : [];
              const nextDetail = markThreadDetailExpanded({
                ...baseThread,
                accountId: baseThread.accountId || sent.accountId,
                subject: sent.subject || baseThread.subject,
                messages: [optimistic, ...prior.filter((message) => String(message.id) !== localId)],
                messageCount: Math.max(baseThread.messageCount || 0, prior.length) + 1,
                snippet: optimistic.snippet || baseThread.snippet,
                date: optimistic.date || baseThread.date,
              });
              const rowKey = threadRowKey(nextDetail) || openKey;
              rememberPendingSent(rowKey, optimistic);
              setSelectedId(rowKey);
              setDetail(nextDetail);
              putDetailCache(nextDetail);
              const ordered = sortedMessages(nextDetail, threadOrder);
              setCollapsedBodies(defaultCollapsedBodies(ordered));
              // Force a background refresh that re-merges pending locals if the
              // provider chain is still catching up.
              void openThread(nextDetail, { backgroundRefresh: true });
              void loadList({ soft: true });
              // Gmail sent copies can take ~1 min to become readable via /api/mail/:id.
              for (const delayMs of [4_000, 12_000, 30_000, 60_000]) {
                window.setTimeout(() => {
                  if (selectedIdRef.current !== rowKey) return;
                  void openThread(nextDetail, { backgroundRefresh: true });
                }, delayMs);
              }
            }}
            onSavedDraft={(_saved: SavedDraftInfo) => {
              setComposeDraft(null);
              void loadList({ soft: true });
            }}
            onScheduled={(_scheduled: ScheduledSendInfo) => {
              setComposeDraft(null);
              void loadList({ soft: true });
            }}
            accountLabel={accountOptionLabel}
          />
        ) : !selectedId ? (
          <div className="mail-empty-reader">
            <p>Select a message</p>
          </div>
        ) : (
          <MailReaderErrorBoundary
            resetKey={selectedId}
            onReset={() => {
              const match = visibleThreads.find((thread) => threadRowKey(thread) === selectedId);
              if (match) void openThread(match);
            }}
          >
            {detailLoading && !detail ? (
              <p className="muted pad">Loading message…</p>
            ) : phoneLayout && detail ? (
              <>
                {detailLoading && isMailThreadHydrated(detail) ? (
                  <p className="muted mail-reader-loading">Updating message…</p>
                ) : null}
                <div
                  className={swipeDragging ? "mail-swipe-stage dragging" : "mail-swipe-stage"}
                  ref={swipeStageRef}
                  data-mail-swipe="stage"
                  onTouchStart={onSwipeTouchStart}
                  onTouchMove={onSwipeTouchMove}
                  onTouchEnd={onSwipeTouchEnd}
                  onTouchCancel={() => resetSwipe()}
                  onMouseDown={(event) => {
                    if (event.button !== 0) return;
                    onSwipePointerDown(event.clientX, event.clientY, event.target);
                  }}
                  onMouseMove={(event) => {
                    if (swipeStartX.current == null) return;
                    onSwipePointerMove(event.clientX, event.clientY);
                  }}
                  onMouseUp={(event) => onSwipePointerUp(event.clientX)}
                  onMouseLeave={() => {
                    if (swipeStartX.current != null) resetSwipe(true);
                  }}
                >
                  {prevPeek ? (
                    <div
                      className="mail-swipe-peek prev full"
                      aria-hidden="true"
                      data-mail-swipe-peek="prev"
                      style={peekTransitionStyle(
                        `translateX(calc(-100% + ${swipeOffset}px))`,
                      )}
                    >
                      {renderSwipeReader(prevPeek, false)}
                    </div>
                  ) : null}
                  {nextPeek ? (
                    <div
                      className="mail-swipe-peek next full"
                      aria-hidden="true"
                      data-mail-swipe-peek="next"
                      style={peekTransitionStyle(
                        `translateX(calc(100% + ${swipeOffset}px))`,
                      )}
                    >
                      {renderSwipeReader(nextPeek, false)}
                    </div>
                  ) : null}
                  <div
                    className="mail-swipe-current"
                    data-mail-swipe-peek="current"
                    style={peekTransitionStyle(`translateX(${swipeOffset}px)`)}
                  >
                    {renderSwipeReader(detail, true)}
                  </div>
                </div>
              </>
            ) : (
              <>
                {detailLoading ? (
                  <p className="muted mail-reader-loading">Updating message…</p>
                ) : null}
                {detail ? renderSwipeReader(detail, true) : null}
              </>
            )}
          </MailReaderErrorBoundary>
        )}
      </section>

      {emailsDrawerOpen ? (
        <div
          className="mail-emails-scrim phone-only"
          onClick={() => setEmailsDrawerOpen(false)}
        >
          <aside
            className="mail-emails-drawer"
            aria-label="Emails"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mail-emails-drawer-head">
              <strong>Emails</strong>
              <button
                type="button"
                className="mail-emails-close"
                aria-label="Close Emails menu"
                onClick={() => setEmailsDrawerOpen(false)}
              >
                <X size={16} />
              </button>
            </div>

            <div className="module-drawer-body">
              <p className="mail-nav-kicker">Emails</p>

              <div className="mail-tree">
                <div className="mail-tree-branch">
                  <button
                    type="button"
                    className={
                      accountFilter === "all"
                        ? "mail-tree-account active"
                        : "mail-tree-account"
                    }
                    aria-expanded={Boolean(treeExpanded.all)}
                    onClick={() => toggleTreeBranch("all")}
                  >
                    <span className="mail-tree-caret" aria-hidden>
                      {treeExpanded.all ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                    <span className="mail-tree-stack">
                      <strong>All Inboxes</strong>
                      <small>
                        {accounts.length} account{accounts.length === 1 ? "" : "s"}
                      </small>
                    </span>
                  </button>
                  {treeExpanded.all ? (
                    <div className="mail-tree-folders">
                      {MAIL_FOLDERS.map((item) => (
                        <button
                          key={`all-${item.id}`}
                          type="button"
                          className={
                            accountFilter === "all" && folder === item.id
                              ? "mail-tree-folder active"
                              : "mail-tree-folder"
                          }
                          onClick={() => selectAccountFolder("all", item.id)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                {accounts.map((account) => {
                  const open = Boolean(treeExpanded[account.id]);
                  return (
                    <div key={account.id} className="mail-tree-branch">
                      <button
                        type="button"
                        className={
                          accountFilter === account.id
                            ? "mail-tree-account active"
                            : "mail-tree-account"
                        }
                        title={account.email}
                        aria-expanded={open}
                        onClick={() => toggleTreeBranch(account.id)}
                      >
                        <span className="mail-tree-caret" aria-hidden>
                          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </span>
                        <span className="mail-tree-stack">
                          <strong>{providerLabel(account)}</strong>
                        <small className="mail-email-truncate">{accountOptionLabel(account)}</small>
                      </span>
                    </button>
                    {open ? (
                      <div className="mail-tree-folders">
                        {foldersForAccountNav(account.id, providerFolders).map((item) => (
                          <button
                            key={`${account.id}-${item.id}`}
                            type="button"
                            className={
                              accountFilter === account.id && folder === item.id
                                ? "mail-tree-folder active"
                                : "mail-tree-folder"
                            }
                            onClick={() => selectAccountFolder(account.id, item.id)}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            </div>

            <ModuleDrawerAdd
              to="/settings/connections/mail"
              label="Add inbox"
              onNavigate={() => setEmailsDrawerOpen(false)}
            />
          </aside>
        </div>
      ) : null}

      {attachPreview ? (
        <MailAttachmentPreview
          items={attachPreview.items}
          index={attachPreview.index}
          onIndexChange={(next) =>
            setAttachPreview((prev) => (prev ? { ...prev, index: next } : prev))
          }
          onClose={() => setAttachPreview(null)}
        />
      ) : null}
    </div>
  );
}
